import * as i0 from '@angular/core';
import { NgModule, Injectable, Optional, Inject, ErrorHandler, Pipe, APP_INITIALIZER, Directive, Input, HostListener } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationStart, NavigationEnd, convertToParamMap } from '@angular/router';
export { ActivatedRoute, RouterLink } from '@angular/router';
import * as i1$1 from '@angular/common/http';
import { HttpClientModule, HttpRequest, HttpResponse, HttpHeaders, HttpParams, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i1$2 from '@angular/common';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { of as of$1 } from 'rxjs/internal/observable/of';
import { filter, map, retry, shareReplay, catchError, concatMap, take } from 'rxjs/operators';
import { format } from 'date-fns';
import { of, BehaviorSubject as BehaviorSubject$1, isObservable, map as map$1, catchError as catchError$1, throwError, Observable, ReplaySubject, defer } from 'rxjs';
import * as vlq from 'vlq';
import { v4 } from 'uuid';
import hash from 'hash-it';
import * as i1$3 from '@angular/platform-browser';
import * as i1$4 from 'keycloak-angular';
import { KeycloakEventType, KeycloakService, KeycloakAngularModule } from 'keycloak-angular';
import { Subscription } from 'rxjs/internal/Subscription';
import { tick } from '@angular/core/testing';

/* eslint-disable complexity */
/* eslint-disable no-undefined */
/**
 * Valores por defecto para la configuración de Google Analytics
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @type {IAmsGoogleAnalyticsConfig}
 */
const amsGoogleAnaliticsConfigDefaultValues = {
    gtmAuth: undefined,
    gtmPreview: undefined,
    gtmResourcePath: 'https://www.googletagmanager.com/gtm.js',
    id: null,
    key: null
};
/**
 * Clase para la gestión de Google Analytics
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsGoogleAnalyticsConfig
 * @typedef {AmsGoogleAnalyticsConfig}
 * @implements {IAmsGoogleAnalyticsConfig}
 */
class AmsGoogleAnalyticsConfig {
    /**
     * Creates an instance of IAmsGoogleAnalyticsConfig.
     *
     * @author Antonio Garcia Gomez
     * @param {?IAmsGoogleAnalyticsConfig} config Datos de configuración a usar si no tenemos una en la variable appEnv
     *                                            del window (fichero app-env.js). Si no se proporcionan unos datos de
     *                                            configuración y existen a nivel de appENv, se ponen valores por defecto
     */
    constructor(config) {
        let confEnv = {};
        // Si la aplicación se ha desplegado en algún entorno,
        // recuperamos los datos de configuración del fichero app-env.js
        if (window.appEnv) {
            const env = window.appEnv;
            if (env.analytics) {
                confEnv = { ...env.analytics };
            }
            // Si no tenemos app-env.js miramos en las variables de entorno de la aplicación
        }
        else if (config) {
            if (config.environment?.analytics) {
                confEnv = { ...config.environment.analytics };
            }
            else {
                confEnv = { ...config };
            }
        }
        // Configuramos estos campos con el valor que tengamos en este orden:
        // 1º Fichero configuración desplegado appEnv.js
        // 2º Configuración recibida por parámetro (1º a través de la campo 'environment'
        //    y si no lo tiene, a través de los otros campos de configuración)
        // 3º Configuración por defecto
        this.setConfigValue(confEnv, 'id');
        this.setConfigValue(confEnv, 'gtmResourcePath');
    }
    /**
     * Configura el campo recibido con el valor que tengamos en alguno de los siguientes puntos de configuración en este orden:
     *  1º Configuración recibida en el fichero appEnv.js o el parámetro de configuración del constructor
     *  2º Configuración por defecto
     *
     * @author Antonio Garcia Gomez
     * @param {IAmsGoogleAnalyticsConfig} confEnv Configuración recibida en el fichero appEnv.js
     *                                            o el parámetro de configuración del constructor
     * @param {string} campo Campo que queremos configurar
     */
    setConfigValue(confEnv, campo) {
        let valueL;
        if (confEnv?.[campo] !== undefined) {
            valueL = confEnv[campo];
        }
        else {
            valueL = amsGoogleAnaliticsConfigDefaultValues[campo];
        }
        this[campo] = valueL;
    }
}

/**
 * Módulo para la gestión de Google Analytics
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsAnalyticsModule
 * @typedef {AmsAnalyticsModule}
 */
class AmsAnalyticsModule {
    /**
     * Método para configurar el módulo de Analitics
     *
     * @author Antonio Garcia Gomez
     * @public
     * @static
     * @param {?IAmsGoogleAnalyticsConfig} config El objeto con los datos de configuración
     * @returns {ModuleWithProviders<AmsAnalyticsModule>} El módulo configurado
     * @throws Error si no se pasa el id de Google Analitics
     * @example
     *   NgModule({
     *     imports: [
     *       AmsAnalyticsModule.forRoot({ id: 'GTM-DEMO' }),
     *   }
     */
    static forRoot(config) {
        // Construímos una configuración fusionada con la de por defecto, la que recibimos y si hubiera un fichero appEnv.js
        const fullConfig = new AmsGoogleAnalyticsConfig(config);
        if (!fullConfig.id) {
            throw new Error('Se debe incluir el id de la aplicación en la inyección del módulo del Google Analytics');
        }
        return {
            ngModule: AmsAnalyticsModule,
            providers: [{ provide: 'amsTagManagerConfig', useValue: fullConfig }]
        };
    }
}
AmsAnalyticsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsAnalyticsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AmsAnalyticsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: AmsAnalyticsModule });
AmsAnalyticsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsAnalyticsModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsAnalyticsModule, decorators: [{
            type: NgModule
        }] });

const MILISECONDS = 1000;
/**
 * Servicio para la gestión de Google Analytics
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsAnalyticsService
 * @typedef {AmsAnalyticsService}
 * @implements {IAnalyticsService}
 */
class AmsAnalyticsService {
    /**
     * Creates an instance of AmsAnalyticsService.
     *
     * @author Antonio Garcia Gomez
     * @class
     * @param {IAmsGoogleAnalyticsConfig} [config] Configuración del módulo
     * @param {string} googleTagManagerId Parámetro googleTagManagerId para ser sustituido por el recibido en el objeto de configuración
     * @param {string} googleTagManagerAuth Parámetro googleTagManagerAuth para ser sustituido por el recibido en el objeto de configuración
     * @param {string} googleTagManagerPreview Parámetro googleTagManagerPreview para ser sustituido por el recibido
     *                                         en el objeto de configuración
     * @param {string} googleTagManagerResourcePath Parámetro googleTagManagerResourcePath para ser sustituido por el
     *                                              recibido en el objeto de configuración
     * @param {string} googleTagManagerCSPNonce Parámetro googleTagManagerCSPNonce para ser sustituido por el recibido
     *                                          en el objeto de configuración
     * @param {Router} router Parámetro router para ser sustituido por el recibido en el objeto de configuración
     */
    constructor(config = { id: null }, googleTagManagerId, googleTagManagerAuth, googleTagManagerPreview, googleTagManagerResourcePath, googleTagManagerCSPNonce, router) {
        this.config = config;
        this.googleTagManagerId = googleTagManagerId;
        this.googleTagManagerAuth = googleTagManagerAuth;
        this.googleTagManagerPreview = googleTagManagerPreview;
        this.googleTagManagerResourcePath = googleTagManagerResourcePath;
        this.googleTagManagerCSPNonce = googleTagManagerCSPNonce;
        this.router = router;
        this.isLoaded = false;
        this.browserGlobals = {
            windowRef: () => window,
            documentRef: () => document
        };
        this.config = {
            ...this.config,
            id: googleTagManagerId || this.config.id,
            gtmAuth: googleTagManagerAuth || this.config.gtmAuth,
            gtmPreview: googleTagManagerPreview || this.config.gtmPreview,
            gtmResourcePath: googleTagManagerResourcePath || this.config.gtmResourcePath
        };
        if (this.config.id === null) {
            throw new Error('Google tag manager ID not provided.');
        }
    }
    /**
     * Añade el script de GTM al header de la página que tiene embebido el servicio
     *
     * @author Antonio Garcia Gomez
     * @returns {Promise<boolean>} Una promesa con el resultado de añadir el GTM as DOM
     */
    addGTMToDom() {
        return new Promise((resolve, reject) => {
            if (this.isLoaded) {
                return resolve(this.isLoaded);
            }
            const doc = this.browserGlobals.documentRef();
            this.pushOnDataLayer({
                gtmStart: new Date().getTime(),
                event: 'gtm.js'
            });
            const gtmScript = doc.createElement('script');
            gtmScript.id = 'GTMscript';
            gtmScript.async = true;
            let applyGtmQueryParam;
            if (this.config.gtmResourcePath) {
                applyGtmQueryParam = this.config.gtmResourcePath;
            }
            else {
                applyGtmQueryParam = 'https://www.googletagmanager.com/gtm.js';
            }
            gtmScript.src = this.applyGtmQueryParams(applyGtmQueryParam);
            gtmScript.addEventListener('load', () => resolve((this.isLoaded = true)));
            gtmScript.addEventListener('error', () => reject(false));
            if (this.googleTagManagerCSPNonce) {
                gtmScript.setAttribute('nonce', this.googleTagManagerCSPNonce);
            }
            doc.head.insertBefore(gtmScript, doc.head.firstChild);
        });
    }
    /**
     * Devuelve la configuración del módulo de analytics
     *
     * @author Antonio Garcia Gomez
     * @returns {IAmsGoogleAnalyticsConfig} La configuración del analytics
     */
    getConfig() {
        return this.config;
    }
    /**
     * Inicia la gestión del GTM añadiendo el script al DOM y registrando los eventos del Router
     *
     * @author Antonio Garcia Gomez
     */
    init() {
        this.addGTMToDom().then(() => this.registerRoutingEvents());
    }
    /**
     * Devuelve una promesa con el resultado de intentar añadir el item recibido como parámetro
     * al dataLayer del GTM
     *
     * @param {object} item El objeto que queremos añadir al dataLayer
     * @returns {Promise<void>} La promesa con el resultado de añadir el objeto item al dataLayer
     */
    pushTag(item) {
        return new Promise((resolve, reject) => {
            // Si aún no se ha añadido el script del GTM, se añade
            if (!this.isLoaded) {
                this.addGTMToDom()
                    .then(() => {
                    this.pushOnDataLayer(item);
                    return resolve();
                })
                    .catch(() => reject());
            }
            else {
                this.pushOnDataLayer(item);
                return resolve();
            }
        });
    }
    /**
     * Añade por cada evento del Router un objeto al dataLayer de GTM con los siguientes datos.
     * Event = 'page', pageName = url del evento del Router y timeLoading = la hora de carga
     *
     * @author Antonio Garcia Gomez
     */
    registerRoutingEvents() {
        this.router.events.forEach((item) => {
            if (item instanceof NavigationStart) {
                this.dateStart = new Date().getTime();
            }
            if (item instanceof NavigationEnd) {
                const gtmTag = {
                    event: 'page',
                    timeLoading: (new Date().getTime() - this.dateStart) / MILISECONDS,
                    pageName: item.url
                };
                this.pushTag(gtmTag);
            }
        });
    }
    /**
     * Añade los parámetros incluidos en la configuración a la url que recibimos como parámetro
     * Forma una URL con los parámetros incluidos en la configuración siguiendo el formato (?...&...&...)
     *
     * @private
     * @param {string} url La URL que queremos gestionar
     * @returns {string} La URL con los parámetros añadidos
     */
    applyGtmQueryParams(url) {
        if (url.indexOf('?') === -1) {
            url += '?';
        }
        return (url +
            Object.keys(this.config)
                .filter((k) => this.config[k])
                .map((k) => `${k}=${this.config[k]}`)
                .join('&'));
    }
    /**
     * Proporciona acceso al objeto dataLayer del window. La Data Layer o capa de datos de Tag Manager
     * es el medio estándar utilizado para comunicar el sitio web (o app) con Google Tag Manager
     *
     * @private
     * @returns {any[]} El dataLayer del window
     */
    getDataLayer() {
        const windowRef = this.browserGlobals.windowRef();
        windowRef.dataLayer = windowRef.dataLayer || [];
        return windowRef.dataLayer;
    }
    /**
     * Añade al dataLayer del GTM el objeto recibido como parámetro
     *
     * @private
     * @param {object} obj El objeto que queremos añadir al dataLayer
     */
    pushOnDataLayer(obj) {
        const dataLayer = this.getDataLayer();
        dataLayer.push(obj);
    }
}
AmsAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsAnalyticsService, deps: [{ token: 'amsTagManagerConfig', optional: true }, { token: 'googleTagManagerId', optional: true }, { token: 'googleTagManagerAuth', optional: true }, { token: 'googleTagManagerPreview', optional: true }, { token: 'googleTagManagerResourcePath', optional: true }, { token: 'googleTagManagerCSPNonce', optional: true }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
AmsAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['amsTagManagerConfig']
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['googleTagManagerId']
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['googleTagManagerAuth']
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['googleTagManagerPreview']
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['googleTagManagerResourcePath']
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['googleTagManagerCSPNonce']
                }] }, { type: i1.Router }]; } });

/* eslint-disable no-undefined */
/**
 * Clase para la configuración del controlador global de errores
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsErrorHandlerConfig
 * @typedef {AmsErrorHandlerConfig}
 * @implements {IAmsErrorHandlerConfig}
 */
class AmsErrorHandlerConfig {
    /**
     * Creates an instance of AmsErrorHandlerConfig.
     *
     * @author Antonio Garcia Gomez
     * @param {?IAmsErrorHandlerConfig} config Datos de configuración a usar si no
     *                                         tenemos una en la variable appEnv del window (fichero app-env.js).
     */
    constructor(config) {
        let confEnv = {};
        // Si la aplicación se ha desplegado en algún entorno,
        // recuperamos los datos de configuración del fichero app-env.js
        if (window.appEnv) {
            const env = window.appEnv;
            if (env.errorHandler) {
                confEnv = { ...env.errorHandler };
            }
            // Si no tenemos app-env.js miramos en las variables de entorno de la aplicación
        }
        else if (config) {
            if (config.environment?.errorHandler) {
                confEnv = { ...config.environment.errorHandler };
            }
            else {
                confEnv = { ...config };
            }
        }
        // Configuramos estos campos con el valor que tengamos en este orden:
        // 1º Fichero configuración desplegado appEnv.js
        // 2º Configuración recibida por parámetro (1º a través de la campo 'environment'
        //    y si no lo tiene, a través de los otros campos de configuración)
        this.setConfigValue(confEnv, 'appName');
        this.setConfigValue(confEnv, 'client');
    }
    /**
     * Configura el campo recibido con el valor que tengamos en alguno de los siguientes puntos de configuración en este orden:
     *  1º Fichero configuración desplegado appEnv.js o fichero environment de la aplicación
     *  2º Configuración recibida por parámetro
     *
     * @author Antonio Garcia Gomez
     * @param {IAmsErrorHandlerConfig} confEnv Configuración recibida en el fichero appEnv.js o el parámetro de configuración del constructor
     * @param {string} campo Campo que queremos configurar
     */
    setConfigValue(confEnv, campo) {
        this[campo] = confEnv?.[campo];
    }
}

const MAX_ARRAY_SIZE$1 = 150;
/**
 * Servicio que expone un Observable con una cola de errores
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsErrorHandlerService
 * @typedef {AmsErrorHandlerService}
 * @implements {IErrorHandlerService}
 */
class AmsErrorHandlerService {
    /**
     * Creates an instance of AmsErrorHandlerService.
     *
     * @author Antonio Garcia Gomez
     */
    constructor() {
        this.errorsBS = new BehaviorSubject([]);
        this.maxArrSize = MAX_ARRAY_SIZE$1;
    }
    /**
     * Recupera un observable que devuelve los errores registrados
     *
     * @author Antonio Garcia Gomez
     * @readonly
     * @type {Observable<IAmsErrorEntry[]>}
     */
    get errors$() {
        return this.errorsBS.asObservable();
    }
    /**
     * Método para añadir una entrada de log al observable
     *
     * @author Antonio Garcia Gomez
     * @param {IAmsErrorEntry} errorEntry Los datos de la entrada del error en el manejador
     */
    addErrorEntry(errorEntry) {
        let errors = this.errorsBS.getValue();
        // Controlamos que no se exceda del tamaño máximo establecido
        if (errors.length >= this.maxArrSize) {
            errors = errors.slice(errors.length - this.maxArrSize, 0);
        }
        errors.push(errorEntry);
        this.errorsBS.next(errors);
    }
}
AmsErrorHandlerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandlerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AmsErrorHandlerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandlerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandlerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Enumerado para los nivel del logger permisos
 *
 * @author Antonio Garcia Gomez
 * @enum {number}
 */
var AmsLoggerLevel;
(function (AmsLoggerLevel) {
    AmsLoggerLevel[AmsLoggerLevel["TRACE"] = 0] = "TRACE";
    AmsLoggerLevel[AmsLoggerLevel["DEBUG"] = 1] = "DEBUG";
    AmsLoggerLevel[AmsLoggerLevel["INFO"] = 2] = "INFO";
    AmsLoggerLevel[AmsLoggerLevel["LOG"] = 3] = "LOG";
    AmsLoggerLevel[AmsLoggerLevel["WARN"] = 4] = "WARN";
    AmsLoggerLevel[AmsLoggerLevel["ERROR"] = 5] = "ERROR";
    AmsLoggerLevel[AmsLoggerLevel["FATAL"] = 6] = "FATAL";
    AmsLoggerLevel[AmsLoggerLevel["OFF"] = 7] = "OFF";
})(AmsLoggerLevel || (AmsLoggerLevel = {}));

const SPACE = ' ';
/**
 * Clase que deben implementar las entradas en el Logger
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @abstract
 * @class LoggerEntry
 * @typedef {LoggerEntry}
 * @implements {IAmsLoggerEntry}
 */
class LoggerEntry {
    /**
     * Devuelve el mensaje que necesitamos mostrar por consola
     *
     * @author Antonio Garcia Gomez
     * @returns {string} El mensaje a mostrar en la consola del navegador
     */
    getFormattedMessage() {
        let ret = this.formattedDate + SPACE + AmsLoggerLevel[this.level];
        if (this.fileName) {
            ret += ` [${this.fileName}:${this.lineNumber}:${this.columnNumber}] - `;
        }
        return ret;
    }
}

/* eslint-disable indent */
/**
 * Formato de fecha por defecto a registrar en la entrada de la consola
 *
 * @author Antonio Garcia Gomez
 * @type {"yyyy-MM-dd HH:mm:ss"}
 */
const FORMATO_FECHA = 'yyyy-MM-dd HH:mm:ss';
// export const styleWarn = '\x1B[49;33;22m';
// export const styleError = '\x1B[49;31;22m';
// export const styleFatal = '\x1B[49;91;22m';
// export const styleClean = '\x1B[m';
/**
 * Clase para la gestión de una entrada del Logger
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerEntry
 * @typedef {AmsLoggerEntry}
 * @augments {LoggerEntry}
 */
class AmsLoggerEntry extends LoggerEntry {
    /**
     * Creates an instance of AmsLoggerEntry.
     *
     * @author Antonio Garcia Gomez
     */
    constructor() {
        super();
        // Public Properties
        this.entryDate = new Date();
        this.message = '';
        this.formattedMessage = '';
        this.extraInfo = [];
        this.formattedDate = format(this.entryDate, FORMATO_FECHA);
        // this.logWithDate = true;
        // this.times = 1;
    }
}

/* eslint-disable @typescript-eslint/naming-convention */
var AmsLoggerPublisherType;
(function (AmsLoggerPublisherType) {
    AmsLoggerPublisherType[AmsLoggerPublisherType["LOCAL_STORAGE"] = 0] = "LOCAL_STORAGE";
    AmsLoggerPublisherType[AmsLoggerPublisherType["SESSION_STORAGE"] = 1] = "SESSION_STORAGE";
    AmsLoggerPublisherType[AmsLoggerPublisherType["CONSOLE"] = 2] = "CONSOLE";
})(AmsLoggerPublisherType || (AmsLoggerPublisherType = {}));

/**
 * Constante para el uso de colores en función del nivel del logger
 *
 * @author Antonio Garcia Gomez
 * @type {AmsLoggerColorScheme}
 */
const DEFAULT_COLOR_SCHEME = [
    'purple',
    'teal',
    'gray',
    'darkgray',
    'darkorange',
    'red',
    'red',
    '' // OFF
];

const CODE_ALL = 22;
const CODE_FIREFOX = 1014;
/**
 * Clase que deben implementar todos los publicadores del Logger
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @abstract
 * @class LoggerPublisher
 * @typedef {LoggerPublisher}
 */
class LoggerPublisher {
    constructor() {
        this.location = '';
    }
    /**
     * Determines whether an error is a QuotaExceededError.
     *
     * Browsers love throwing slightly different variations of QuotaExceededError
     * (this is especially true for old browsers/versions), so we need to check
     * different fields and values to ensure we cover every edge-case.
     *
     * @author Antonio Garcia Gomez
     * @param {unknown} err The error to check
     * @returns {boolean} Is the error a QuotaExceededError?
     */
    isQuotaExceededError(err) {
        return (err instanceof DOMException &&
            // everything except Firefox
            (err.code === CODE_ALL ||
                // Firefox
                err.code === CODE_FIREFOX ||
                // test name field too, because code might not be present
                // everything except Firefox
                err.name === 'QuotaExceededError' ||
                // Firefox
                err.name === 'NS_ERROR_DOM_QUOTA_REACHED'));
    }
}

/* eslint-disable no-undefined */
/**
 * Clase para la gestión del publicador de la consola de javascript
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerConsole
 * @typedef {AmsLoggerConsole}
 * @augments {LoggerPublisher}
 */
class AmsLoggerConsole extends LoggerPublisher {
    /**
     * Creates an instance of AmsLoggerConsole.
     *
     * @author Antonio Garcia Gomez
     * @param {IAmsLoggerConfig} config La configuración del logger
     */
    constructor(config) {
        // Must call `super()`from derived classes
        super();
        // Configuración del logger
        this.config = config;
        // Set type
        this.tipo = AmsLoggerPublisherType.CONSOLE;
    }
    /**
     * Limpia la consola
     *
     * @author Antonio Garcia Gomez
     * @returns {Observable<boolean>} Un observable con el resultado de limpiar la consola
     */
    clear() {
        console.clear();
        return of(true);
    }
    /**
     * Recupera el color para usar en la consola
     *
     * @author Antonio Garcia Gomez
     * @param {AmsLoggerLevel} level EL nivel de traza
     * @returns {(string)} El color asociado al nivel de traza
     */
    getColor(level) {
        const configColorScheme = this.config.colorScheme ?? DEFAULT_COLOR_SCHEME;
        return configColorScheme[level];
    }
    /**
     * Añade una entrada de log a la consola
     *
     * @author Antonio Garcia Gomez
     * @param {AmsLoggerEntry} entry La entrada a añadir
     * @returns {Observable<boolean>} Un observable con un buleano sobre si ha añadido la entrada a la consola
     */
    log(entry) {
        // const mensaje = '%c' + entry.buildLogStringConsole();
        let metaString;
        let color;
        let response = false;
        // Si no tenemos desconectado el logger
        if (entry.level !== AmsLoggerLevel.OFF) {
            // Recuperamos la fecha/hora, el nivel de log y los datos del fichero, de la línea y columna del log
            metaString = entry.getFormattedMessage();
            // Recuperamos el color
            color = this.getColor(entry.level);
            // Make sure additional isn't null or undefined so that ...additional doesn't error
            const additional = entry.extraInfo || [];
            // Hacemos un log en la consola del navegador
            // for (let i = 0; i < entry.times; i += 1) {
            switch (entry.level) {
                case AmsLoggerLevel.WARN:
                    console.warn(`%c${metaString}`, `color:${color}`, entry.message, ...additional);
                    break;
                case AmsLoggerLevel.ERROR:
                case AmsLoggerLevel.FATAL:
                    console.error(`%c${metaString}`, `color:${color}`, entry.message, ...additional);
                    break;
                case AmsLoggerLevel.DEBUG:
                case AmsLoggerLevel.INFO:
                case AmsLoggerLevel.LOG:
                case AmsLoggerLevel.TRACE:
                    console.log(`%c${metaString}`, `color:${color}`, entry.message, ...additional);
                    break;
                default:
                    console.log(`%c${metaString}`, `color:${color}`, entry.message, ...additional);
            }
            response = true;
        }
        return of(response);
    }
}

/**
 * Clave a usar para almacenar los datos en el Local Storage
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @type {"logging"}
 */
const LOCAL_STORAGE_KEY = 'logging';
/**
 * Clase para la gestión del publicador del Local Storage de Javascript
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerLocalStorage
 * @typedef {AmsLoggerLocalStorage}
 * @augments {LoggerPublisher}
 */
class AmsLoggerLocalStorage extends LoggerPublisher {
    constructor() {
        // Must call `super()`from derived classes
        super();
        // Set type
        this.tipo = AmsLoggerPublisherType.LOCAL_STORAGE;
        // Set location
        this.location = LOCAL_STORAGE_KEY;
    }
    /**
     * Limpia el contenido del local storage para la key predefinida
     *
     * @author Antonio Garcia Gomez
     * @returns {Observable<boolean>} Un observable con un buleano sobre si ha limpiado la entrada del local storage
     */
    clear() {
        localStorage.removeItem(this.location);
        return of(true);
    }
    /**
     * Añade una entrada de log al local storage
     *
     * @author Antonio Garcia Gomez
     * @param {AmsLoggerEntry} entry La entrada a añadir
     * @returns {Observable<boolean>} Un observable con un buleano sobre si ha añadido la entrada al local storage
     */
    log(entry) {
        let ret = false;
        let values;
        try {
            // Get previous values from local storage
            const localStorageValue = localStorage.getItem(this.location);
            if (localStorageValue) {
                values = JSON.parse(localStorageValue);
            }
            else {
                values = [];
            }
            // Add new log entry to array
            values.push(entry);
            // Store array into local storage
            try {
                localStorage.setItem(this.location, JSON.stringify(values));
            }
            catch (err) {
                if (this.isQuotaExceededError(err)) {
                    // Handle the case where there wasn't enough space to store the
                    // item in localStorage.
                    this.clear();
                }
                else {
                    // Handle the case where the localStorage API is not supported.
                    console.log(err);
                }
            }
            // Set return value
            ret = true;
        }
        catch (ex) {
            // Display error in console
            console.log(ex);
        }
        return of(ret);
    }
}

/**
 * Clave a usar para almacenar los datos en el Session Storage
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @type {"logging"}
 */
const SESSION_STORAGE_KEY$1 = 'logging';
/**
 * Clase para la gestión del publicador del Session Storage de Javascript
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerSessionStorage
 * @typedef {AmsLoggerSessionStorage}
 * @augments {LoggerPublisher}
 */
class AmsLoggerSessionStorage extends LoggerPublisher {
    constructor() {
        // Must call `super()`from derived classes
        super();
        // Set type
        this.tipo = AmsLoggerPublisherType.SESSION_STORAGE;
        // Set location
        this.location = SESSION_STORAGE_KEY$1;
    }
    // localStorageSpace = (): void => {
    //   let data = '';
    //   // console.log("LOCAL STORAGE", localStorage)
    //   for (const key in sessionStorage) {
    //     if (sessionStorage.hasOwnProperty(key)) {
    //       data += sessionStorage[key];
    //       console.log(key + ' = ' + ((sessionStorage[key].length * 16) / (8 * 1024)).toFixed(2) + ' KB');
    //     }
    //   }
    //   console.log(
    //     data
    //       ? '\n' + 'Session Storage Total space used: ' + ((data.length * 16) / (8 * 1024)).toFixed(2) + ' KB'
    //       : 'Empty (0 KB)'
    //   );
    //   // console.log(data ? 'Approx. space remaining: ' + (5120 - ((data.length * 16)/(8 * 1024)).toFixed(2)) + ' KB' : '5 MB');
    // };
    /**
     * Limpia el contenido del session storage para la key predefinida
     *
     * @author Antonio Garcia Gomez
     * @returns {Observable<boolean>} Un observable con un buleano sobre si ha limpiado la entrada del session storage
     */
    clear() {
        sessionStorage.removeItem(this.location);
        return of(true);
    }
    /**
     * Añade una entrada de log al session storage
     *
     * @author Antonio Garcia Gomez
     * @param {AmsLoggerEntry} entry La entrada a añadir
     * @returns {Observable<boolean>} Un observable con un buleano sobre si ha añadido la entrada al session storage
     */
    log(entry) {
        let ret = false;
        let values;
        try {
            // Get previous values from session storage
            const localStorageValue = sessionStorage.getItem(this.location);
            if (localStorageValue) {
                values = JSON.parse(localStorageValue);
            }
            else {
                values = [];
            }
            // Add new log entry to array
            values.push(entry);
            // Store array into local storage
            try {
                // console.log('Añadiendo entrada');
                sessionStorage.setItem(this.location, JSON.stringify(values));
            }
            catch (err) {
                if (this.isQuotaExceededError(err)) {
                    // Handle the case where there wasn't enough space to store the
                    // item in localStorage.
                    this.clear();
                }
                else {
                    // Handle the case where the localStorage API is not supported.
                    console.log(err);
                }
            }
            // this.localStorageSpace();
            // Set return value
            ret = true;
        }
        catch (ex) {
            // Display error in console
            console.log(ex);
        }
        return of(ret);
    }
}

/**
 * Valores por defecto del Logger
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @type {IAmsLoggerConfig}
 */
const AMS_LOGGER_DEFAULT_VALUES = {
    // mapper service //
    customHttpHeaders: undefined,
    customHttpParams: undefined,
    enableSourceMaps: false,
    serverLoggingUrl: '/api/log',
    // global //
    environment: {},
    logToConsole: true,
    logToLocalStorage: false,
    logToSessionStorage: false,
    logToServer: true,
    // logWithDate: true,
    serverLogLevel: AmsLoggerLevel.ERROR
};
/**
 * Clase para  la configuración del Logger
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerConfig
 * @typedef {AmsLoggerConfig}
 * @implements {IAmsLoggerConfig}
 */
class AmsLoggerConfig {
    /**
     * Creates an instance of AmsLoggerConfig.
     *
     * @author Antonio Garcia Gomez
     * @param {?IAmsLoggerConfig} config Datos de configuración a usar si no tenemos una en la variable appEnv del
     *                                   window (fichero app-env.js). Si no se proporcionan unos datos de configuración
     *                                   y existen a nivel de appENv, se ponen valores por defecto
     */
    constructor(config) {
        // mapper service //
        this.enableSourceMaps = false;
        let confEnv = {};
        // Nos aseguramos de tener la propiedad production del environment informada
        // Si recibimos el environment en la configuración...
        if (config?.environment) {
            this.environment = config.environment;
            // Si no viene la propiedad production, le ponemos valor por defecto
            if (this.environment.production === undefined) {
                this.environment.production = true;
            }
            // Si no recibimos la propiedad environment
        }
        else {
            // Le ponemos valor por defecto
            this.environment = { production: true };
        }
        // Configuramos estos campos con el valor que tengamos en este orden:
        //  1º Fichero configuración desplegado appEnv.js
        //  2º Configuración recibida en la propiedad environment (que debería ser el environment de la aplicación)
        //  3º Configuración recibida por parámetro
        //  4º Configuración por defecto
        // Si la aplicación se ha desplegado en algún entorno,
        // recuperamos los datos de configuración del fichero app-env.js
        if (window.appEnv) {
            const env = window.appEnv;
            confEnv = { ...env.logger };
            // Si no tenemos app-env.js miramos en las variables de entorno de la aplicación
        }
        else if (config) {
            if (config.environment?.logger) {
                confEnv = { ...config.environment.logger };
            }
            else {
                confEnv = { ...config };
            }
        }
        // Configuramos estos campos con el valor que tengamos en este orden:
        // 1º Fichero configuración desplegado appEnv.js
        // 2º Configuración recibida por parámetro (1º a través de la campo 'environment'
        //    y si no lo tiene, a través de los otros campos de configuración)
        // 3º Configuración por defecto
        this.setConfigValue(confEnv, 'enableSourceMaps');
        this.setConfigValue(confEnv, 'logToConsole');
        this.setConfigValue(confEnv, 'logToLocalStorage');
        this.setConfigValue(confEnv, 'logToSessionStorage');
        this.setConfigValue(confEnv, 'logWithDate');
        this.setConfigValue(confEnv, 'serverLogLevel');
        // Valores no configurables
        this.logToServer = AMS_LOGGER_DEFAULT_VALUES.logToServer;
        this.serverLoggingUrl = AMS_LOGGER_DEFAULT_VALUES.serverLoggingUrl;
    }
    /**
     * Configura el campo recibido con el valor que tengamos en alguno de los siguientes puntos de configuración en este orden:
     *  1º Fichero configuración desplegado appEnv.js
     *  2º Configuración recibida por parámetro
     *  3º Configuración por defecto
     *
     * @author Antonio Garcia Gomez
     * @param {IAmsLoggerConfig} confEnv Configuración recibida en el fichero appEnv.js o el parámetro de configuración del constructor
     * @param {string} campo Campo que queremos configurar
     */
    setConfigValue(confEnv, campo) {
        let valueL;
        if (confEnv?.[campo] !== undefined) {
            valueL = confEnv[campo];
        }
        else {
            valueL = AMS_LOGGER_DEFAULT_VALUES[campo];
        }
        this[campo] = valueL;
    }
}

/* eslint-disable no-undefined */
/**
 * Módulo para la gestión del Logger
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerModule
 * @typedef {AmsLoggerModule}
 */
class AmsLoggerModule {
    /**
     * Crea la instancia del módulo con la posibilidad de añadirle una configuración
     *
     * @author Antonio Garcia Gomez
     * @public
     * @static
     * @param {?IAmsLoggerConfig} [config] La configuración que queramos añadir (la propiedad environment dentro del
     *                                 fichero de configuración es un valor obligatorio)
     * @returns {ModuleWithProviders<AmsLoggerModule>} El módulo con el valor de la configuración inyectada
     * @example
     *   NgModule({
     *     imports: [
     *       AmsLoggerModule.forRoot({
     *         environment,
     *         logToLocalStorage: true,
     *         logToSessionStorage: true,
     *         logToConsole: true,
     *         serverLogLevel: AmsLoggerLevel.DEBUG
     *      })]
     *   }
     */
    static forRoot(config) {
        // Construímos una configuración fusionada con la de por defecto, la que recibimos y si hubiera un fichero appEnv.js
        const fullConfig = new AmsLoggerConfig(config);
        return {
            ngModule: AmsLoggerModule,
            providers: [{ provide: 'amsLoggerConfig', useValue: fullConfig }]
        };
    }
}
AmsLoggerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AmsLoggerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerModule, imports: [HttpClientModule] });
AmsLoggerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerModule, imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [HttpClientModule],
                    providers: [],
                    exports: []
                }]
        }] });

/* eslint-disable @typescript-eslint/member-ordering */
const MAX_ARRAY_SIZE = 150;
/**
 * Servicio para la gestión de los logs como observable. Expone logMessages$ como observable con todas las entradas del logger
 *
 * @author Antonio Garcia Gomez
 * @class LogViewService
 * @typedef {LogViewService}
 * @implements {ILoggerViewService}
 */
class AmsLoggerViewService {
    /**
     * Creates an instance of AmsLoggerViewService.
     *
     * @author Antonio Garcia Gomez
     */
    constructor() {
        this.logMessagesBS = new BehaviorSubject$1([]);
        this.maxArrSize = MAX_ARRAY_SIZE;
        this.logMessages$ = this.logMessagesBS.asObservable();
    }
    /**
     * Método para añadir una entrada de log al observable
     *
     * @author Antonio Garcia Gomez
     * @param {IAmsLoggerEntry} loggerEntry Los datos de la entrada del log a añadir
     */
    addLogData(loggerEntry) {
        let messages = this.logMessagesBS.getValue();
        if (messages.length >= this.maxArrSize) {
            messages = messages.slice(messages.length - this.maxArrSize, 0);
        }
        messages.push(loggerEntry);
        this.logMessagesBS.next(messages);
    }
}
AmsLoggerViewService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerViewService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AmsLoggerViewService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerViewService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerViewService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/* eslint-disable no-undefined */
/**
 * Servicio para la gestión de publicación de los suscriptores adscritos al Logger
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerPublishersService
 * @typedef {AmsLoggerPublishersService}
 * @implements {ILoggerPublishersService}
 */
class AmsLoggerPublishersService {
    /**
     * Creates an instance of LogPublishersService.
     *
     * @author Antonio Garcia Gomez
     * @param {AmsLoggerConfig} config La configuración del logger inyectada en la creación del módulo
     */
    constructor(config) {
        this.config = config;
        // Publicadores
        this.publishers = [];
        this.config = config || new AmsLoggerConfig(config);
        // Build publishers arrays
        this.buildPublishers();
    }
    /**
     * Construye el array de publicadores de los logs
     *
     * @author Antonio Garcia Gomez
     */
    buildPublishers() {
        if (this.config.logToConsole) {
            // Create instance of AmsLoggerConsole Class
            this.publishers.push(new AmsLoggerConsole(this.config));
        }
        if (this.config.logToLocalStorage) {
            // Create instance of `AmsLoggerLocalStorage` Class
            this.publishers.push(new AmsLoggerLocalStorage());
        }
        if (this.config.logToSessionStorage) {
            // Create instance of `AmsLoggerSessionStorage` Class
            this.publishers.push(new AmsLoggerSessionStorage());
        }
    }
    /**
     * Devuelve la configuración del logger contruida a partir de la recibida en la construcción del módulo
     * la configuración por defecto y la de la configuración de la aplicación (fichero appEnv.js)
     *
     * @author Antonio Garcia Gomez
     * @returns {*} La configuración del logger construida con la recibida en la construcción del módulo,
     * la de por defecto y la de la configuración de la aplicación (fichero appEnv.js)
     */
    getConfig() {
        return this.config;
    }
}
AmsLoggerPublishersService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerPublishersService, deps: [{ token: 'amsLoggerConfig', optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
AmsLoggerPublishersService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerPublishersService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerPublishersService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['amsLoggerConfig']
                }] }]; } });

/* eslint-disable @typescript-eslint/quotes */
/**
 * Servicio para recuperar la posición dentro del código de un log
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerMapperService
 * @typedef {AmsLoggerMapperService}
 * @implements {IMapperService}
 */
class AmsLoggerMapperService {
    constructor(httpBackend) {
        this.httpBackend = httpBackend;
        /** cache for source maps, key is source map location, ie. 'http://localhost:4200/main.js.map' */
        this.sourceMapCache = new Map();
        /** cache for specific log position, key is the dist position, ie 'main.js:339:21' */
        this.logPositionCache = new Map();
    }
    /**
     * Returns the log position of the caller
     * If sourceMaps are enabled, it attemps to get the source map from the server, and use that to parse the position
     *
     * @author Antonio Garcia Gomez
     * @public
     * @param {IAmsLoggerConfig} config La configuración del logger
     * @returns {Observable<IAmsLoggerLogPosition>} Un observable con los datos de posición del log
     */
    getLogPosition(config) {
        const stackLine = this.getStackLine(config);
        // if we were not able to parse the stackLine, just return an empty Log Position
        if (!stackLine) {
            return of({ fileName: '', lineNumber: 0, columnNumber: 0 });
        }
        // console.log('stackLine', stackLine);
        const logPosition = this.getLocalPosition(stackLine);
        // console.log('logPosition', logPosition);
        if (!config.enableSourceMaps) {
            return of(logPosition);
        }
        const sourceMapLocation = this.getSourceMapLocation(stackLine);
        return this.getSourceMap(sourceMapLocation, logPosition);
    }
    /**
     * Recupera la posición del caller sin usar sourceMaps
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {string} stackLine La línea de código
     * @returns {IAmsLoggerLogPosition} Los datos de la posición del log
     */
    getLocalPosition(stackLine) {
        // strip base path, then parse filename, line, and column, stackline looks like this :
        // Firefox
        // handleLog@http://localhost:4200/main.js:1158:29
        // Chrome and Edge
        // at AppComponent.handleLog (app.component.ts:38)
        // console.log('stackLine: ', stackLine);
        const positionStartIndex = stackLine.lastIndexOf('/');
        let positionEndIndex = stackLine.indexOf(')');
        if (positionEndIndex < 0) {
            positionEndIndex = undefined;
        }
        const position = stackLine.substring(positionStartIndex + 1, positionEndIndex);
        const dataArray = position.split(':');
        if (dataArray.length === 3 && dataArray[1] && dataArray[2]) {
            return { fileName: dataArray[0], lineNumber: +dataArray[1], columnNumber: +dataArray[2] };
        }
        return { fileName: 'unknown', lineNumber: 0, columnNumber: 0 };
    }
    /**
     * Convert the map generated position to source position
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {SourceMap} sourceMap El sourceMap
     * @param {IAmsLoggerLogPosition} position La posición
     * @returns {IAmsLoggerLogPosition} El mapping
     */
    getMapping(sourceMap, position) {
        // => ';' indicates end of a line
        // => ',' separates mappings in a line
        // decoded mapping => [ generatedCodeColumn, sourceFileIndex, sourceCodeLine, sourceCodeColumn, nameIndex ]
        let sourceFileIndex = 0; // second field
        let sourceCodeLine = 0; // third field
        let sourceCodeColumn = 0; // fourth field
        const lines = sourceMap.mappings.split(';');
        for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
            // reset column position to 0 after each line
            let generatedCodeColumn = 0;
            // decode sections in line
            const columns = lines[lineIndex].split(',');
            for (let columnIndex = 0; columnIndex < columns.length; columnIndex += 1) {
                const decodedSection = vlq.decode(columns[columnIndex]);
                if (decodedSection.length >= 4) {
                    // update relative positions
                    generatedCodeColumn += decodedSection[0];
                    sourceFileIndex += decodedSection[1];
                    sourceCodeLine += decodedSection[2];
                    sourceCodeColumn += decodedSection[3];
                }
                // check if matching map
                if (lineIndex === position.lineNumber) {
                    if (generatedCodeColumn === position.columnNumber) {
                        // matching column and line found
                        return {
                            fileName: sourceMap.sources[sourceFileIndex],
                            lineNumber: sourceCodeLine,
                            columnNumber: sourceCodeColumn
                        };
                    }
                    else if (columnIndex + 1 === columns.length) {
                        // matching column not found, but line is correct
                        return { fileName: sourceMap.sources[sourceFileIndex], lineNumber: sourceCodeLine, columnNumber: 0 };
                    }
                }
            }
        }
        // failed if reached
        return { fileName: 'unknown', lineNumber: 0, columnNumber: 0 };
    }
    /**
     * does the http get request to get the source map
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {string} sourceMapLocation La posición del sourceMap
     * @param {IAmsLoggerLogPosition} distPosition dist
     * @returns {Observable<IAmsLoggerLogPosition>} Un Observable con los datos de situación del log
     */
    getSourceMap(sourceMapLocation, distPosition) {
        const req = new HttpRequest('GET', sourceMapLocation);
        const distPositionKey = `${distPosition.fileName}:${distPosition.lineNumber}:${distPosition.columnNumber}`;
        // if the specific log position is already in cache return it
        if (this.logPositionCache.has(distPositionKey)) {
            return this.logPositionCache.get(distPositionKey);
        }
        // otherwise check if the source map is already cached for given source map location
        if (!this.sourceMapCache.has(sourceMapLocation)) {
            if (!this.httpBackend) {
                console.error("Ams Logger : Can't get sourcemap because HttpBackend is not provided. You need to import HttpClientModule");
                this.sourceMapCache.set(sourceMapLocation, of(null));
            }
            else {
                // obtain the source map if not cached
                this.sourceMapCache.set(sourceMapLocation, this.httpBackend.handle(req).pipe(filter((e) => e instanceof HttpResponse), map((httpResponse) => httpResponse.body), retry(3), shareReplay(1)));
            }
        }
        // at this point the source map is cached, use it to get specific log position mapping
        const logPosition$ = this.sourceMapCache.get(sourceMapLocation).pipe(map((sourceMap) => {
            // sourceMap can be null if HttpBackend is not provided for example
            if (!sourceMap) {
                return distPosition;
            }
            // map generated position to source position
            return this.getMapping(sourceMap, distPosition);
        }), catchError(() => of(distPosition)), shareReplay(1));
        // store specific log position in cache for given dest position and return it
        this.logPositionCache.set(distPositionKey, logPosition$);
        return logPosition$;
    }
    /**
     * Gets the URL of the sourcemap (the URL can be relative or absolute, it is browser dependant)
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {string} stackLine La línea de la pila
     * @returns {string} LA posición dentro del sourcemap
     */
    getSourceMapLocation(stackLine) {
        const file = this.getTranspileLocation(stackLine);
        const mapFullPath = file.substring(0, file.lastIndexOf(':'));
        return mapFullPath.substring(0, mapFullPath.lastIndexOf(':')) + '.map';
    }
    /**
     * Get the stackline of the original caller
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {IAmsLoggerConfig} config La configuración del logger
     * @returns {string} null si la stackline no se encuentra
     */
    getStackLine(config) {
        const error = new Error();
        try {
            // noinspection ExceptionCaughtLocallyJS
            throw error;
        }
        catch (e) {
            try {
                // Here are different examples of stacktrace
                // Firefox (last line is the user code, the 4 first are ours):
                // getStackLine@http://localhost:4200/main.js:358:23
                // getCallerDetails@http://localhost:4200/main.js:557:44
                // _log@http://localhost:4200/main.js:830:28
                // debug@http://localhost:4200/main.js:652:14
                // handleLog@http://localhost:4200/main.js:1158:29
                // Chrome and Edge (last line is the user code):
                // Error
                // at Function.getStackLine (ngx-logger.js:329)
                // at NGXMapperService.getCallerDetails (ngx-logger.js:528)
                // at Ams Logger._log (ngx-logger.js:801)
                // at Ams Logger.info (ngx-logger.js:631)
                // at AppComponent.handleLog (app.component.ts:38)
                let defaultProxy = 4; // We make 4 functions call before getting here
                const firstStackLine = error.stack.split('\n')[0];
                if (!firstStackLine.includes('.js:')) {
                    // The stacktrace starts with no function call (example in Chrome or Edge)
                    defaultProxy = defaultProxy + 1;
                }
                return error.stack.split('\n')[defaultProxy + (config.proxiedSteps || 0)];
            }
            catch (e2) {
                return null;
            }
        }
    }
    /**
     * Recupera la línea de la pila del código traspilado
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {string} stackLine La línea de código
     * @returns {string} La línea de la pila del código traspilado
     */
    getTranspileLocation(stackLine) {
        // Example stackLine:
        // Firefox : getStackLine@http://localhost:4200/main.js:358:23
        // Chrome and Edge : at Function.getStackLine (ngx-logger.js:329)
        let locationStartIndex = stackLine.indexOf('(');
        if (locationStartIndex < 0) {
            locationStartIndex = stackLine.lastIndexOf('@');
            if (locationStartIndex < 0) {
                locationStartIndex = stackLine.lastIndexOf(' ');
            }
        }
        let locationEndIndex = stackLine.indexOf(')');
        if (locationEndIndex < 0) {
            locationEndIndex = undefined;
        }
        return stackLine.substring(locationStartIndex + 1, locationEndIndex);
    }
}
AmsLoggerMapperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerMapperService, deps: [{ token: i1$1.HttpBackend, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
AmsLoggerMapperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerMapperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerMapperService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpBackend, decorators: [{
                    type: Optional
                }] }]; } });

/* eslint-disable jsdoc/require-param-type */
const STRINGIFY_SPACES = 2;
/**
 * Servicio para la gestión de la llamada HTTP con los datos de la entrada del log
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerServerService
 * @typedef {AmsLoggerServerService}
 * @implements {ILoggerServerService}
 */
class AmsLoggerServerService {
    /**
     * Creates an instance of AmsLoggerServerService.
     *
     * @author Antonio Garcia Gomez
     * @param {HttpBackend} httpBackend La instancia del HttpBackend
     */
    constructor(httpBackend) {
        this.httpBackend = httpBackend;
    }
    /**
     * Modifica la petición HTTP antes de mandarla al servidor
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {HttpRequest<any>} httpRequest Petición por defecto
     * @returns {(HttpRequest<any> | Observable<HttpRequest<any>>)} Petición modificada
     */
    alterHttpRequest(httpRequest) {
        return httpRequest;
    }
    /**
     * Customiza los datos a enviar al API
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {IAmsLoggerEntry} loggerEntry Los datos del logger
     * @returns {IAmsLoggerEntry} Los datos que se mandaran al API en el body de la petición
     */
    customiseRequestBody(loggerEntry) {
        // In our API the body is not customised
        return loggerEntry;
    }
    /**
     * Manda la petición al servidor
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {string} url La url para registrar la petición en el servidor
     * @param {*} logContent El contenido de la entrada del logger que queremos registrar
     * @param {} options Datos que queremos usar en la llamada HTTP
     * @param {HttpHeaders} options.headers Las cabeceras de la llamada HTTP
     * @param {boolean} options.reportProgress El indicador de reporte remoto
     * @param {HttpParams} options.params Parámetros adicionales de la llamada
     * @param {'arraybuffer' | 'blob' | 'json' | 'text'} options.responseType Tipo de respuesta
     * @param {boolean} options.withCredentials Indicador de si hay que usar credenciale
     * @returns {Observable<any>} El observable con el resultado de la llamada HTTP
     */
    logOnServer(url, logContent, options) {
        // if (!this.httpBackend) {
        //   console.error(
        //     `AmsLogger : No se puede hace el log en el servidor porque el HttpBackend
        //      no se ha podido recuperar. Se debe importar el HttpClientModule`
        //   );
        //   return of(null);
        // }
        // HttpBackend skips all HttpInterceptors
        // They may log errors using this service causing circular calls
        const defaultRequest = new HttpRequest('POST', url, logContent, options || {});
        let finalRequest = of(defaultRequest);
        const alteredRequest = this.alterHttpRequest(defaultRequest);
        if (isObservable(alteredRequest)) {
            finalRequest = alteredRequest;
        }
        else if (alteredRequest) {
            finalRequest = of(alteredRequest);
        }
        else {
            console.warn('AmsLogger : alterHttpRequest ha devuelto una petición no válido. Usando la petición de por defecto');
        }
        return finalRequest.pipe(concatMap((req) => {
            let rtv;
            if (!req) {
                console.warn('AmsLogger : alterHttpRequest returned an invalid request (observable). Using default one instead');
                rtv = this.httpBackend.handle(defaultRequest);
            }
            else {
                rtv = this.httpBackend.handle(req);
            }
            return rtv;
        }), filter((e) => e instanceof HttpResponse), map((httpResponse) => httpResponse.body));
    }
    // /**
    //  * Transforms the additional parameters to avoid any json error when sending the data to the server
    //  * Basically it just replaces unstringifiable object to a string mentioning an error
    //  *
    //  * @author Antonio Garcia Gomez
    //  * @protected
    //  * @param {any[]} additional The additional data to be sent
    //  * @returns {any[]} The additional data secured
    //  */
    // protected secureAdditionalParameters(additional: any[]): any[] {
    //   if (additional === null || additional === undefined) {
    //     return null;
    //   }
    //   return additional.map((next, idx) => {
    //     try {
    //       if (next instanceof Error) {
    //         return this.secureErrorObject(next);
    //       }
    //       // We just want to make sure the JSON can be parsed, we do not want to actually change the type
    //       if (typeof next === 'object') {
    //         JSON.stringify(next);
    //       }
    //       return next;
    //     } catch (e) {
    //       return `The additional[${idx}] value could not be parsed using JSON.stringify().`;
    //     }
    //   });
    // }
    /**
     * Transforms an error object into a readable string (taking only the stack)
     * This is needed because JSON.stringify would return "{}"
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {Error} err The error object
     * @returns {string} The stack of the error
     */
    secureErrorObject(err) {
        return err?.stack || '';
    }
    /**
     * Transforma el mensaje para que pueda ser enviado al servidor de una forma segura
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {*} message El mensaje que queremos mandar
     * @returns {string} El mensaje securizado
     */
    secureMessage(message) {
        let rtv;
        try {
            if (message instanceof Error) {
                rtv = this.secureErrorObject(message);
            }
            if (typeof message !== 'string') {
                rtv = JSON.stringify(message, null, STRINGIFY_SPACES);
            }
            else {
                rtv = message;
            }
        }
        catch (e) {
            rtv = 'The provided "message" value could not be parsed with JSON.stringify().';
        }
        return rtv;
    }
    /**
     * Manda al API del servidor la petición tratada para que el mensaje sea seguro, con parámetros adicionales si hacen falta
     * y con el body customizado si es necesario
     *
     * @author Antonio Garcia Gomez
     * @public
     * @param {IAmsLoggerEntry} loggerEntry La entrada del logger a mandar al servidor
     * @param {?IAmsLoggerConfig} [config] La configuración del logger
     */
    sendToServer(loggerEntry, config) {
        // Copying loggerEntry locally because we don't want to change the object for the caller
        const localMetadata = { ...loggerEntry };
        // Securizamos los parámetros adicionales
        // localMetadata.additional = this.secureAdditionalParameters(localMetadata.additional);
        // Securizamos el mensaje
        localMetadata.message = this.secureMessage(localMetadata.message);
        // Permite a los usuarios customizar los datos que se mandan al API
        const requestBody = this.customiseRequestBody(localMetadata);
        // Cabeceras de la petición
        const headers = config?.customHttpHeaders || new HttpHeaders();
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }
        if (config?.serverLoggingUrl) {
            this.logOnServer(config.serverLoggingUrl, requestBody, {
                headers,
                params: config.customHttpParams || new HttpParams(),
                responseType: config.httpResponseType || 'json',
                withCredentials: config.withCredentials || false
            })
                .pipe(catchError((err) => of(err)))
                .subscribe();
        }
    }
}
AmsLoggerServerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerServerService, deps: [{ token: i1$1.HttpBackend, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
AmsLoggerServerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerServerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerServerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpBackend, decorators: [{
                    type: Optional
                }] }]; } });

/**
 * Servicio para la gestión del Logger. Expone los métodos debug, fatal, info, log, warn y error para los
 * distintos tipos de trazas que se quieran dejar
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsLoggerService
 * @typedef {AmsLoggerService}
 * @implements {ILoggerService}
 */
class AmsLoggerService {
    /**
     * Creates an instance of AmsLoggerService.
     *
     * @author Antonio Garcia Gomez
     * @param {AmsLoggerPublishersService} loggerPublishersService El servicio para la gestión de los publicadores
     * @param {AmsLoggerViewService} loggerViewService El servicio para la gestión del observable del logger
     * @param {AmsLoggerMapperService} mapperService El servicio para la gestión de la línea de código del logger
     * @param {AmsLoggerServerService} loggerServerService El servicio para la gestión de las llamadas HTTP a servidor
     */
    constructor(loggerPublishersService, loggerViewService, mapperService, loggerServerService) {
        this.loggerPublishersService = loggerPublishersService;
        this.loggerViewService = loggerViewService;
        this.mapperService = mapperService;
        this.loggerServerService = loggerServerService;
        this.level = AmsLoggerLevel.INFO;
        // Set publishers
        this.publishers = this.loggerPublishersService.publishers;
        this.config = this.loggerPublishersService.config;
    }
    /**
     * Función para hacer un log en nivel DEBUG
     *
     * @author Antonio Garcia Gomez
     * @param {string} msg El mensaje del que queremos hacer el log
     * @param {...any[]} optionalParams Si queremos añadir datos adicionales al log
     * @returns {(IAmsLoggerEntry | void)} La entrada que se intenta añadir a los publicadores o void si no debe publicar nada
     *                                  (en función del entorno y los permisos)
     */
    debug(msg, ...optionalParams) {
        return this.writeToLog(msg, AmsLoggerLevel.DEBUG, optionalParams);
    }
    /**
     * Función para hacer un log en nivel ERROR
     *
     * @author Antonio Garcia Gomez
     * @param {string} msg El mensaje del que queremos hacer el log
     * @param {...any[]} optionalParams Si queremos añadir datos adicionales al log
     * @returns {(IAmsLoggerEntry | void)} La entrada que se intenta añadir a los publicadores o void si no debe publicar nada
     *                                  (en función del entorno y los permisos)
     */
    error(msg, ...optionalParams) {
        return this.writeToLog(msg, AmsLoggerLevel.ERROR, optionalParams);
    }
    /**
     * Función para hacer un log en nivel FATAL
     *
     * @author Antonio Garcia Gomez
     * @param {string} msg El mensaje del que queremos hacer el log
     * @param {...any[]} optionalParams Si queremos añadir datos adicionales al log
     * @returns {(IAmsLoggerEntry | void)} La entrada que se intenta añadir a los publicadores o void si no debe publicar nada
     *                                  (en función del entorno y los permisos)
     */
    fatal(msg, ...optionalParams) {
        return this.writeToLog(msg, AmsLoggerLevel.FATAL, optionalParams);
    }
    /**
     * Devuelve la configuración del logger
     *
     * @author Antonio Garcia Gomez
     * @returns {IAmsLoggerConfig} La configuración del logger
     */
    getConfig() {
        return this.config;
    }
    /**
     * Función para hacer un log en nivel INFO
     *
     * @author Antonio Garcia Gomez
     * @param {string} msg El mensaje del que queremos hacer el log
     * @param {...any[]} optionalParams Si queremos añadir datos adicionales al log
     * @returns {(IAmsLoggerEntry | void)} La entrada que se intenta añadir a los publicadores o void si no debe publicar nada
     *                                  (en función del entorno y los permisos)
     */
    info(msg, ...optionalParams) {
        return this.writeToLog(msg, AmsLoggerLevel.INFO, optionalParams);
    }
    /**
     * Función para hacer un log en nivel LOG
     *
     * @author Antonio Garcia Gomez
     * @param {string} msg El mensaje del que queremos hacer el log
     * @param {...any[]} optionalParams Si queremos añadir datos adicionales al log
     * @returns {(IAmsLoggerEntry | void)} La entrada que se intenta añadir a los publicadores o void si no debe publicar nada
     *                                  (en función del entorno y los permisos)
     */
    log(msg, ...optionalParams) {
        return this.writeToLog(msg, AmsLoggerLevel.LOG, optionalParams);
    }
    // /**
    //  * Función para hacer un log en nivel TRACE
    //  *
    //  * @author Antonio Garcia Gomez
    //  * @param {string} msg El mensaje del que queremos hacer el log
    //  * @param {...any[]} optionalParams Si queremos añadir datos adicionales al log
    //  * @returns {(IAmsLoggerEntry | void)} La entrada que se intenta añadir a los publicadores o void si no debe publicar nada
    //  *                                  (en función del entorno y los permisos)
    //  */
    // trace(msg: string, ...optionalParams: any[]): IAmsLoggerEntry | void {
    //   return this.writeToLog(msg, AmsLoggerLevel.TRACE, optionalParams);
    // }
    /**
     * Función para hacer un log en nivel WARN
     *
     * @author Antonio Garcia Gomez
     * @param {string} msg El mensaje del que queremos hacer el log
     * @param {...any[]} optionalParams Si queremos añadir datos adicionales al log
     * @returns {(IAmsLoggerEntry | void)} La entrada que se intenta añadir a los publicadores o void si no debe publicar nada
     *                                  (en función del entorno y los permisos)
     */
    warn(msg, ...optionalParams) {
        return this.writeToLog(msg, AmsLoggerLevel.WARN, optionalParams);
    }
    /**
     * Devuelve si el nivel de traza permite hacer el LOG
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {AmsLoggerLevel} level El nivel que queremos evaluar
     * @returns {boolean} True si se permite hacer el log o no
     */
    shouldLog(level) {
        let ret = false;
        if (this.config && this.config.serverLogLevel) {
            if (level >= this.config.serverLogLevel && level !== AmsLoggerLevel.OFF) {
                ret = true;
            }
        }
        return ret;
    }
    /**
     * Escribe y registra el log si el nivel de traza lo permite
     *
     * @author Antonio Garcia Gomez
     * @protected
     * @param {any} msg El mensaje a registrar en el publicador
     * @param {AmsLoggerLevel} level El nivel del mensaje a publicar
     * @param {any[]} params Parámetros adicionales que queramos mostrar
     */
    writeToLog(msg, level, params) {
        // Si debe mandar el log los publicadores
        if (this.shouldLog(level)) {
            // Crea la entrada de log
            const entry = new AmsLoggerEntry();
            // Fija los valores de la misma
            // Si tenemos un error del error handler
            if (msg.message) {
                entry.message = msg.message;
                entry.stack = msg.stack;
            }
            else {
                entry.message = msg;
            }
            entry.level = level;
            entry.extraInfo = params;
            // entry.logWithDate = this.logWithDate;
            // Registra el nivel de logging como un observable
            this.loggerViewService.addLogData(entry);
            this.mapperService
                .getLogPosition(this.config)
                .pipe(take(1), catchError(() => {
                // Itera por todos los publicadores registrados y hace el log
                for (const logger of this.publishers) {
                    logger.log(entry);
                }
                return of$1();
            }))
                .subscribe((logPosition) => {
                if (logPosition) {
                    entry.fileName = logPosition.fileName;
                    entry.lineNumber = logPosition.lineNumber;
                    entry.columnNumber = logPosition.columnNumber;
                }
                // Almacenamos el mensaje formateado con fecha, lugar del mensaje...
                entry.formattedMessage = entry.getFormattedMessage() + entry.message;
                // Si tenemos un error, hacemos la llamada HTTP con la entrada del logger
                if (level === AmsLoggerLevel.ERROR) {
                    // Mandamos entrada del log de error al servidor
                    this.loggerServerService.sendToServer(entry, this.config);
                }
                // Itera por todos los publicadores registrados y hace el log
                for (const logger of this.publishers) {
                    // Sólo escribimos en los logger si no estamos en producción o es el logger de la consola
                    if (logger.tipo === AmsLoggerPublisherType.CONSOLE || this.config?.environment?.production === false) {
                        logger.log(entry);
                    }
                }
            });
            return entry;
        }
    }
}
AmsLoggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerService, deps: [{ token: AmsLoggerPublishersService }, { token: AmsLoggerViewService }, { token: AmsLoggerMapperService }, { token: AmsLoggerServerService }], target: i0.ɵɵFactoryTarget.Injectable });
AmsLoggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsLoggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AmsLoggerPublishersService }, { type: AmsLoggerViewService }, { type: AmsLoggerMapperService }, { type: AmsLoggerServerService }]; } });

/**
 * Manejador global de errores
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsErrorHandler
 * @typedef {AmsErrorHandler}
 * @implements {ErrorHandler}
 */
class AmsErrorHandler {
    /**
     * Creates an instance of AmsErrorHandler.
     *
     * @author Antonio Garcia Gomez
     * @class
     * @param {Location} location El lugar del error
     * @param {AmsErrorHandlerService} amsErrorHandlerService El servicio para la gestión de los errores
     * @param {AmsLoggerService} amsLoggerService El servicio para la gestión de los logs
     * @param {IAmsErrorHandlerConfig} config La configuración recuperada de la conf. del módulo
     */
    constructor(location, amsErrorHandlerService, amsLoggerService, config) {
        this.location = location;
        this.amsErrorHandlerService = amsErrorHandlerService;
        this.amsLoggerService = amsLoggerService;
        this.location = location;
        this.config = config;
        this.setParameters();
    }
    /**
     * Función que gestiona los errores de la aplicación
     *
     * @author Antonio Garcia Gomez
     * @param {*} error El error que queremos gestionar
     */
    handleError(error) {
        const params = {
            appName: this.appName,
            client: this.clientConnected,
            viewError: this.viewerror
        };
        const errorEntry = { error, params };
        // Añadimos una entrada al observable
        this.amsErrorHandlerService.addErrorEntry(errorEntry);
        // Mostramos el error través del logger
        this.amsLoggerService.error(error, params);
        // this.processError(error);
    }
    // /**
    //  * Procesa el error manejado
    //  *
    //  * @author Antonio Garcia Gomez
    //  * @param {*} error El error que se ha controlado
    //  */
    // processError(error: any): void {
    //   console.error(error);
    // }
    /**
     * Establece los parámetros de modelo del error
     *
     * @author Antonio Garcia Gomez
     * @memberof GlobalErrorHandler
     */
    setParameters() {
        this.appName = this.config.appName || 'ams-arquitectura';
        this.clientConnected = this.config.client || 'anonimo';
        this.viewerror = this.location.path();
    }
}
AmsErrorHandler.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandler, deps: [{ token: i1$2.Location }, { token: AmsErrorHandlerService }, { token: AmsLoggerService }, { token: 'config' }], target: i0.ɵɵFactoryTarget.Injectable });
AmsErrorHandler.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandler, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.Location }, { type: AmsErrorHandlerService }, { type: AmsLoggerService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['config']
                }] }]; } });

/**
 * Módulo para la gestión global de errores de una aplicación
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsErrorHandlerModule
 * @typedef {AmsErrorHandlerModule}
 */
class AmsErrorHandlerModule {
    /**
     * Método para configurar el módulo de control de errores
     *
     * @author Antonio Garcia Gomez
     * @static
     * @param {?IAmsErrorHandlerConfig} config EL objeto con la configuración a aplicar
     * @returns {ModuleWithProviders<AmsErrorHandlerModule>} El módulo configurado
     * @throws Error si no se pasa el appName a usar en el control de errores
     * @example
     *   NgModule({
     *     imports: [AmsErrorHandlerModule.forRoot({ appName: 'appPrueba' })]
     *   }
     */
    static forRoot(config) {
        // Construímos una configuración fusionada con la de por defecto, la que recibimos y si hubiera un fichero appEnv.js
        const fullConfig = new AmsErrorHandlerConfig(config);
        if (!fullConfig.appName) {
            throw new Error('Se debe incluir el appName de la aplicación en la inyección del módulo de control de errores');
        }
        return {
            ngModule: AmsErrorHandlerModule,
            providers: [{ provide: 'config', useValue: fullConfig }]
        };
    }
}
AmsErrorHandlerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandlerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AmsErrorHandlerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandlerModule, imports: [HttpClientModule] });
AmsErrorHandlerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandlerModule, providers: [{ provide: ErrorHandler, useClass: AmsErrorHandler }], imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsErrorHandlerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [HttpClientModule],
                    providers: [{ provide: ErrorHandler, useClass: AmsErrorHandler }],
                    exports: []
                }]
        }] });

/**
 * Clase para la configuración de las opciones de HTTP
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsHttpClientExtraOptions
 * @typedef {AmsHttpClientExtraOptions}
 */
class AmsHttpClientExtraOptions {
}

/**
 * Servicio para controlar cuando tenemos alguna petición en curso y así poder
 * controlar si queremos mostrar algún tipo de loader mientras haya peticiones HTTP en curso o
 * alguna gestión parecida
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsHttpPageLoaderService
 * @typedef {AmsHttpPageLoaderService}
 * @implements {IHttpPageLoaderService}
 */
class AmsHttpPageLoaderService {
    constructor() {
        this.jobs = [];
        this.loading = new BehaviorSubject(false);
    }
    /**
     * Recupera un observable que indica si tenemos alguna petición HTTP en curso
     *
     * @author Antonio Garcia Gomez
     * @readonly
     * @type {Observable<boolean>} Un observable que indica si tenemos alguna petición HTTP en curso
     */
    get isLoading$() {
        return this.loading.asObservable();
    }
    /**
     * Indica que debemos descontar una petición HTTP en curso y si no queda ninguna cambia
     * el estado del observable loading a false
     *
     * @author Antonio Garcia Gomez
     */
    hideLoader() {
        this.jobs.pop();
        if (this.jobs.length === 0) {
            this.loading.next(false);
        }
    }
    /**
     * Indica que debemos añadir una petición HTTP a las que están en curso y cambia
     * el estado del observable loading a true
     *
     * @author Antonio Garcia Gomez
     */
    showLoader() {
        this.jobs.push(0);
        this.loading.next(true);
    }
}
AmsHttpPageLoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpPageLoaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AmsHttpPageLoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpPageLoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpPageLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * Servicio para almacenar los tiempos que tardan en ejecutarse las peticiones
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsHttpElapsedTimeService
 * @typedef {AmsHttpElapsedTimeService}
 * @implements {IHttpElapsedTimeService}
 */
class AmsHttpElapsedTimeService {
    /**
     * Creates an instance of AmsHttpElapsedTimeService.
     *
     * @author Antonio Garcia Gomez
     */
    constructor() {
        this.elapsedTime = {};
    }
    /**
     * Devuelve el tiempo empleado en lanzar una petición HTTP
     *
     * @author Antonio Garcia Gomez
     * @param {HttpRequest<any>} request La petición de la que queremos saber el tiempo empleado
     * @returns {Observable<number>} El observable con el tiempo de la petición
     */
    getElapsedTime(request) {
        const key = hash(request).toString();
        const item = this.elapsedTime[key];
        let resp = null;
        if (item) {
            resp = item.time;
        }
        return resp;
    }
    /**
     * Fija el tiempo empleado es lanzar la petición HTTP que le pasamos como parámetro
     *
     * @param {HttpRequest<any>} request La petición HTTP
     * @param {number} elapsedTime El tiempo empleado en lanzar la petición
     */
    setElapsedTime(request, elapsedTime) {
        const key = hash(request).toString();
        this.elapsedTime[key] = { time: of$1(elapsedTime) };
    }
}
AmsHttpElapsedTimeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpElapsedTimeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AmsHttpElapsedTimeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpElapsedTimeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpElapsedTimeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

const FIXED_CHARS = 3;
/**
 * Interceptor para la gestión de las llamadas HTTP:
 *  - Añade la cabecera "X-Request-ID" a todas las peticiones
 *  - Gestiona los errores, capturándolos y lanzado un error
 *  - Permite saber cuando está en curso cualquier llamada HTTP (AmsHttpPageLoaderService) (control de spinner o semejante)
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsHttpInterceptor
 * @typedef {AmsHttpInterceptor}
 * @implements {HttpInterceptor}
 */
class AmsHttpInterceptor {
    /**
     * Creates an instance of AmsHttpInterceptor.
     *
     * @author Antonio Garcia Gomez
     * @param {AmsHttpPageLoaderService} amsPageLoaderService El servicio para indicar si está en medio de peticiones HTTP
     * @param {AmsHttpElapsedTimeService} amsHttpElapsedTimeService El servicio para indicar el tiempo empleado en lanzar las peticiones HTTP
     * @param {AmsLoggerService} amsLoggerService El servicio de logger para poder añadir las peticiones a los logger
     */
    constructor(amsPageLoaderService, amsHttpElapsedTimeService, amsLoggerService) {
        this.amsPageLoaderService = amsPageLoaderService;
        this.amsHttpElapsedTimeService = amsHttpElapsedTimeService;
        this.amsLoggerService = amsLoggerService;
    }
    /**
     * Captura todas las peticiones HTTP para controlar la gestión de peticiones en curso (AmsHttpPageLoaderService)
     * y capturar todos los errores producidos en las llamadas. Además añade la cabecera X-Request-ID a todas las peticiones
     *
     * @param {HttpRequest<any>} req El request de la petición
     * @param {HttpHandler} next El manejador de la petición
     * @returns {Observable<HttpEvent<any>>} Si se produce un error un observable con el error y en caso
     *                                       contrario un Observable con el evento de tipo HttpEvent<any>
     */
    intercept(req, next) {
        const uuid4 = v4();
        const customReq = req.clone({
            headers: req.headers.set('X-Request-ID', `${uuid4}`)
        });
        // Almacenamos el momento justo anterior a procesar la petición
        const start = performance.now();
        return next.handle(customReq).pipe(map$1((event) => {
            // Tiempo que se ha tardado en procesar la petición
            const elapsedTime = performance.now() - start;
            const localeElapsedTime = elapsedTime.toFixed(FIXED_CHARS).replace('.', ',') + ' msg';
            // Guardamos el tiempo empleado en la petición en el servicio
            this.amsHttpElapsedTimeService.setElapsedTime(req, elapsedTime);
            // Tenemos una respuesta HTTP
            if (event instanceof HttpResponse) {
                // Mostramos la petición sólo para nivel DEBUG del logger
                const requestLog = {
                    request: req,
                    response: event,
                    elapsedTime: localeElapsedTime
                };
                this.amsLoggerService.debug(`${req.method.toUpperCase()} - ${req.urlWithParams}`, requestLog);
                // Eliminamos una entrada del contador de peticiones en curso (control del loader)
                this.amsPageLoaderService.hideLoader();
                // Tenemos una petición HTTP
            }
            else {
                // Añadimos una entrada del contador de peticiones en curso (control del loader)
                this.amsPageLoaderService.showLoader();
            }
            return event;
        }), catchError$1((errorParam) => {
            // Eliminamos una entrada del contador de peticiones en curso (control del loader)
            this.amsPageLoaderService.hideLoader();
            let errorMessage = '';
            const err = errorParam.error;
            if (err instanceof ErrorEvent) {
                // client-side error
                errorMessage = `Client-side error: ${err.message}`;
            }
            else {
                // backend error
                errorMessage = `Server-side error: ${errorParam.status + errorParam.message}`;
            }
            return throwError(() => errorMessage);
        }));
    }
}
AmsHttpInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpInterceptor, deps: [{ token: AmsHttpPageLoaderService }, { token: AmsHttpElapsedTimeService }, { token: AmsLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
AmsHttpInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AmsHttpPageLoaderService }, { type: AmsHttpElapsedTimeService }, { type: AmsLoggerService }]; } });

/**
 * Servicio para la gestión de llamadas HTTP. Expone todos los métodos HTTP: DELETE, GET, POST, PUT
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsHttpClientService
 * @typedef {AmsHttpClientService}
 * @implements {IHttpClientService}
 */
class AmsHttpClientService {
    /**
     * Creates an instance of AmsHttpClientService.
     *
     * @author Antonio Garcia Gomez
     * @param {HttpClient} http El cliente HTTP de Angular
     * @param {?AmsHttpClientExtraOptions} [extraOptions] Opciones extra a añadir a la cabecera
     */
    constructor(http, extraOptions) {
        this.http = http;
        this.extraOptions = extraOptions;
    }
    /**
     * Método para hacer una llamada HTTP delete
     *
     * @author Antonio Garcia Gomez
     * @template T El tipo del objeto de respuesta de la llamada al método delete
     * @param {string} endPoint La url a la que hacer la petición delete
     * @param {?IAmsHttpClientRequestOptions} [options] Opciones adicionales a la petición HTTP
     * @returns {Observable<T>} Un observable con el contenido de la respuesta de la llamada HTTP
     */
    delete(endPoint, options) {
        return this.request('delete', endPoint, {}, options);
    }
    /**
     * Método para hacer una llamada HTTP get
     *
     * @author Antonio Garcia Gomez
     * @template T El tipo del objeto de respuesta de la llamada al método get
     * @param {string} endPoint La url a la que hacer la petición get
     * @param {?IAmsHttpClientRequestOptions} [options] Opciones adicionales a la petición HTTP
     * @returns {Observable<T>} Un observable con el contenido de la respuesta de la llamada HTTP
     */
    get(endPoint, options) {
        return this.request('get', endPoint, {}, options);
    }
    /**
     * Método para hacer una llamada HTTP post
     *
     * @author Antonio Garcia Gomez
     * @template T El tipo del objeto de respuesta de la llamada al método post
     * @param {string} endPoint La url a la que hacer la petición post
     * @param {object} params Parámetros que le pasamos al body de la llamada
     * @param {?IAmsHttpClientRequestOptions} [options] Opciones adicionales a la petición HTTP
     * @returns {*} {Observable<T>} Un observable con el contenido de la respuesta de la llamada HTTP
     */
    post(endPoint, params, options) {
        return this.request('post', endPoint, params, options);
    }
    /**
     * Método para hacer una llamada HTTP put
     *
     * @author Antonio Garcia Gomez
     * @template T El tipo del objeto de respuesta de la llamada put
     * @param {string} endPoint La url a la que hacer la petición HTTP
     * @param {object} params Parámetros que le pasamos al body de la llamada
     * @param {?IAmsHttpClientRequestOptions} [options] Opciones adicionales a la petición HTTP
     * @returns {*} {Observable<T>} Un observable con el contenido de la respuesta de la llamada HTTP
     */
    put(endPoint, params, options) {
        return this.request('put', endPoint, params, options);
    }
    /**
     * Método para hacer una llamada HTTP para cualquier método, indicando en el parámetro method el método HTTP
     * que queremos lanzar
     *
     * @author Antonio Garcia Gomez
     * @template T El tipo del objeto de respuesta de la llamada HTTP
     * @param {string} method El método HTTP que queremos lanzar (get, post, put, delete)
     * @param {string} endPoint La url a la que hacer la petición HTTP
     * @param {object} params Parámetros que le pasamos al body de la llamada
     * @param {?IAmsHttpClientRequestOptions} [options] Opciones adicionales a la petición HTTP
     * @returns {Observable<T>} Un observable con el contenido de la respuesta de la llamada HTTP
     */
    request(method, endPoint, params, options) {
        // Indicamos que se está haciendo la carga (si tenemos extraOptions)
        this.setExtraOptionsLoading();
        // Se construyen las opciones que vamos a usar en la llamada
        const opt = options || {
            body: params
        };
        if (params) {
            opt.body = params;
        }
        opt.headers = options && options.headers ? options.headers : new HttpHeaders();
        // Si tenemos opciones que queramos añadir a la cabecera (desde el constructor)
        if (this?.extraOptions?.headers) {
            this.extraOptions.headers.keys().forEach((header) => {
                if (opt?.headers && this?.extraOptions?.headers && this.extraOptions.headers.get(header)) {
                    // Is required by TSLINT
                    opt.headers = opt.headers.append(header, this.extraOptions.headers.get(header));
                }
            });
        }
        // Limpia la autorización de las opciones adicionales que se pueden pasar en la construcción del servicio
        this.cleanExtraOptionsAuthorization();
        return new Observable((observer) => {
            this.setAuthHeader(opt.headers, this.extraOptions.authorization)
                .then((headers) => (opt.headers = headers))
                .then((headers) => {
                opt.headers = headers;
                this.http.request(method, endPoint, opt).subscribe((res) => {
                    observer.next(res);
                }, (res) => {
                    observer.error(res);
                    observer.complete();
                    // Indicamos que se ya no se está haciendo la carga (si tenemos extraOptions)
                    if (this.extraOptions && this.extraOptions.loading) {
                        this.extraOptions.loading.close();
                    }
                }, () => {
                    observer.complete();
                    // Indicamos que se ya no se está haciendo la carga (si tenemos extraOptions)
                    if (this.extraOptions && this.extraOptions.loading) {
                        this.extraOptions.loading.close();
                    }
                });
            });
        });
    }
    /**
     * Método para añadir cabeceras adicionales a la petición HTTP correspondientes a los datos del parámetro auth
     * (suponiendo que el parámetro esté informado debidamente)
     *
     * @author Antonio Garcia Gomez
     * @async
     * @param {HttpHeaders} headers Cabeceras de la llamada a las que se puede añadir una cabecera adicional
     * @param {IAmsHttpClientAuth} auth Objeto con los datos de la cabecera adicional que se quiere añadir
     * @returns {Promise<HttpHeaders>} Las cabeceras de la petición HTTP
     */
    async setAuthHeader(headers, auth) {
        const promise = async (resolve) => {
            if (auth && auth.nameHeader && auth.type && auth.storage && auth.localStorageTokenName) {
                if (auth.storage) {
                    let header = '';
                    let funct = auth.storage.getItem(auth.localStorageTokenName);
                    if (funct instanceof Promise) {
                        funct = await funct;
                    }
                    header = funct ? funct.trim() : null;
                    headers = headers.append(auth.nameHeader, `${auth.type} ${header}`);
                }
                resolve(headers);
            }
            else {
                resolve(headers);
            }
        };
        return new Promise(promise);
    }
    /**
     * Limpia la autorización de las opciones adicionales que se pueden pasar en la construcción del servicio
     *
     * @private
     */
    cleanExtraOptionsAuthorization() {
        if (this.extraOptions && !this.extraOptions.authorization) {
            this.extraOptions.authorization = null;
        }
    }
    /**
     * Indica que se está haciendo la carga en las opciones adicionales que se pueden pasar en la construcción del servicio
     *
     * @author Antonio García Gómez
     * @private
     */
    setExtraOptionsLoading() {
        if (this.extraOptions && this.extraOptions.loading) {
            this.extraOptions.loading.show();
        }
    }
}
AmsHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpClientService, deps: [{ token: i1$1.HttpClient }, { token: AmsHttpClientExtraOptions }], target: i0.ɵɵFactoryTarget.Injectable });
AmsHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpClientService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: AmsHttpClientExtraOptions }]; } });
const amsHttpClientCreator = (http) => new AmsHttpClientService(http);

/**
 * Módulo para la gestión de llamadas HTTP
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsHttpClientModule
 * @typedef {AmsHttpClientModule}
 */
class AmsHttpClientModule {
    /**
     * Use in AppModule: new instance of NGApiClient.
     *
     * @author Antonio Garcia Gomez
     * @public
     * @static
     * @param {AmsHttpClientModuleConfig} [config] Los parámetros de configuración inyectados desde el módulo que contenga este
     * @returns {ModuleWithProviders<AmsHttpClientModule>} El módulo ya configurado con los datos del parámetro config
     */
    static forRoot(config = {}) {
        return {
            ngModule: AmsHttpClientModule,
            providers: [config.loader]
        };
    }
    /**
     * Use in features modules with lazy loading: new instance of AmsHttpClient.
     *
     * @author Antonio Garcia Gomez
     * @public
     * @static
     * @returns {ModuleWithProviders<AmsHttpClientModule>} El módulo ya configurado con los datos del parámetro config
     */
    static forChild() {
        return {
            ngModule: AmsHttpClientModule,
            providers: [AmsHttpClientService]
        };
    }
}
AmsHttpClientModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpClientModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AmsHttpClientModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpClientModule, imports: [HttpClientModule] });
AmsHttpClientModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpClientModule, providers: [AmsHttpClientExtraOptions, { provide: HTTP_INTERCEPTORS, useClass: AmsHttpInterceptor, multi: true }], imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsHttpClientModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule],
                    declarations: [],
                    providers: [AmsHttpClientExtraOptions, { provide: HTTP_INTERCEPTORS, useClass: AmsHttpInterceptor, multi: true }],
                    // providers: [AmsHttpClientExtraOptions, { provide: HTTP_INTERCEPTORS, useClass: AmsHttpInterceptorAux, multi: true }],
                    // providers: [AmsHttpClientExtraOptions, provideHttpClient(withInterceptors([amsHttpInterceptor]))],
                    exports: []
                }]
        }] });

/**
 * Enumerado para identificar el tipo de token de HTTP
 *
 * @author Antonio Garcia Gomez
 * @enum {number}
 */
var AmsHttpClientAuthTypeToken;
(function (AmsHttpClientAuthTypeToken) {
    AmsHttpClientAuthTypeToken["bearer"] = "Bearer";
    AmsHttpClientAuthTypeToken["basic"] = "Basic";
})(AmsHttpClientAuthTypeToken || (AmsHttpClientAuthTypeToken = {}));

/*
 * Public API Surface of AmsHttpClient
 */

/* eslint-disable indent */
/**
 * Pipe para sanear código HTML que Angular por defecto rechaza cómo código HTML inseguro
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsSafePipe
 * @typedef {AmsSafePipe}
 * @implements {PipeTransform}
 */
class AmsSafePipe {
    /**
     * Creates an instance of AmsSafePipe.
     *
     * @author Antonio Garcia Gomez
     * @param {DomSanitizer} sanitizer El sanitize de DOM de Angular
     */
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    /**
     * Función para sanear código HTML
     *
     * @author Antonio Garcia Gomez
     * @public
     * @param {*} value La cadena HTML para sanear
     * @param {string} type El tipo de código que se va a sanear
     * @returns {(SafeHtml | SafeStyle | SafeScript | SafeUrl | SafeResourceUrl)} Devuelve el código HTML saneado
     * @throws Dispara un error si el parámetro tipo no es uno de los siguientes 'html' | 'style' | 'script' | 'url' | 'resourceUrl'
     */
    transform(value, type) {
        let result;
        switch (type) {
            case 'html':
                result = this.bypassSecurityTrustHtml(value);
                break;
            case 'style':
                result = this.bypassSecurityTrustStyle(value);
                break;
            case 'script':
                result = this.bypassSecurityTrustScript(value);
                break;
            case 'url':
                result = this.bypassSecurityTrustUrl(value);
                break;
            case 'resourceUrl':
                result = this.bypassSecurityTrustResourceUrl(value);
                break;
            default:
                throw new Error(`Invalid safe type specified: ${type}`);
        }
        return result;
    }
    /**
     * Función para sanear código html
     *
     * @author Antonio Garcia Gomez
     * @private
     * @param {string} value La cadena a sanear
     * @returns {SafeHtml} Devuelve la cadena saneada
     */
    bypassSecurityTrustHtml(value) {
        return this.sanitizer.bypassSecurityTrustHtml(value);
    }
    /**
     * Función para sanear un resource URL
     *
     * @author Antonio Garcia Gomez
     * @private
     * @param {string} value La cadena del recurso a sanear
     * @returns {SafeResourceUrl} Devuelve el recurso saneado
     */
    bypassSecurityTrustResourceUrl(value) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(value);
    }
    /**
     * Función para sanear un script de javascript
     *
     * @author Antonio Garcia Gomez
     * @private
     * @param {string} value La cadena del script a sanear
     * @returns {SafeScript} Devuelve el script saneado
     */
    bypassSecurityTrustScript(value) {
        return this.sanitizer.bypassSecurityTrustScript(value);
    }
    /**
     * Función para sanear un script de estilos
     *
     * @author Antonio Garcia Gomez
     * @private
     * @param {string} value La cadena del script a sanear
     * @returns {SafeStyle} Devuelve el estilo saneado
     */
    bypassSecurityTrustStyle(value) {
        return this.sanitizer.bypassSecurityTrustStyle(value);
    }
    /**
     * Función para sanear una URL
     *
     * @author Antonio Garcia Gomez
     * @private
     * @param {string} value la cadena a sanear
     * @returns {SafeUrl} Devuelve la url saneada
     */
    bypassSecurityTrustUrl(value) {
        return this.sanitizer.bypassSecurityTrustUrl(value);
    }
}
AmsSafePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsSafePipe, deps: [{ token: i1$3.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
AmsSafePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: AmsSafePipe, name: "safe" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsSafePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'safe' }]
        }], ctorParameters: function () { return [{ type: i1$3.DomSanitizer }]; } });

/**
 * Módulo para la exposición de los distintos pipes de aquitectura
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsPipesModule
 * @typedef {AmsPipesModule}
 */
class AmsPipesModule {
}
AmsPipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsPipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AmsPipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: AmsPipesModule, declarations: [AmsSafePipe], exports: [AmsSafePipe] });
AmsPipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsPipesModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsPipesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AmsSafePipe],
                    exports: [AmsSafePipe]
                }]
        }] });

/* eslint-disable no-empty-function */
const VALIDITY_TIME$1 = 30;
/**
 * Función para la inicialización de keycloak
 *
 * @author Antonio Garcia Gomez
 * @param {KeycloakService} keycloak El servicio de Keycloak
 * @param {IAmsKeycloakFullConfig} config La configuración de Keycloak
 * @returns {() => any} Devuelve la función de inicialización
 */
const initializer = (keycloak, config) => () => new Promise(async (resolve, reject) => {
    if (typeof resolve !== 'function') {
        resolve = () => { };
    }
    if (typeof reject !== 'function') {
        reject = () => { };
    }
    try {
        // Realiza la inicialización a través del servicio de keycloak
        await keycloak.init({
            config: config.config,
            initOptions: config.initOptions,
            enableBearerInterceptor: config.enableBearerInterceptor
        });
        // Recuperamos la instancia de keycloak
        const keycloakAuth = keycloak.getKeycloakInstance();
        // Cuando el token de keycloak expira se vuelve a actualizar
        keycloak.keycloakEvents$.subscribe({
            next: (e) => {
                if (e.type === KeycloakEventType.OnTokenExpired) {
                    keycloak.updateToken(timeTokenExpired(keycloakAuth) - VALIDITY_TIME$1);
                }
            }
        });
        resolve();
    }
    catch (error) {
        reject(error);
    }
});
/**
 * Define una función que devuelve el tiempo de expiración del token de keycloak
 *
 * @author Antonio Garcia Gomez
 * @param {KeycloakInstance} keycloakInstance La instancia de keycloak
 * @returns {number} El tiempo de expiración del token de keycloak
 */
const timeTokenExpired = (keycloakInstance) => {
    // Recuperamos el token de la instancia de keycloak
    const tokenParsed = keycloakInstance.tokenParsed;
    let timeToExpired = 60;
    if (tokenParsed && tokenParsed.exp && tokenParsed.iat) {
        timeToExpired = tokenParsed.exp - tokenParsed.iat;
    }
    return timeToExpired;
};

/* eslint-disable no-undefined */
/**
 * Valores por defecto de initOptions de keycloak
 *
 * @author Antonio Garcia Gomez
 * @type {IAmsKeycloakInitOptions}
 */
const defaultInitOptions = {
    onLoad: 'login-required',
    checkLoginIframe: true,
    pkceMethod: 'S256',
    useNonce: false,
    enableLogging: true
};
/**
 * Función para establecer la configuración de keycloak
 *
 * @author Antonio Garcia Gomez
 * @param {IAmsKeycloakConfig} config Los datos de configuración que recibimos desde la inicialización del módulo
 * @returns {IAmsKeycloakFullConfig} El objeto de configuración de la librería de keycloak
 */
const getAmsKeycloakFullConfig = (config) => {
    let configuration;
    // Si la aplicación se ha desplegado en algún entorno,
    // recuperamos los datos de configuración del fichero app-env.js
    if (window['appEnv']?.keycloak) {
        configuration = window['appEnv'].keycloak;
        // Si no tenemos app-env.js miramos en las variables de entorno de la aplicación
    }
    else if (config?.environment?.keycloak) {
        configuration = config.environment.keycloak;
        // Si no tenemos ninguna, la configuración por defecto (sólo válida para test puesto
        // que es obligatorio pasar alguna de la anteriores)
    }
    else {
        configuration = config;
    }
    let enableBearer;
    if (configuration?.enableBearerInterceptor !== undefined) {
        enableBearer = configuration.enableBearerInterceptor;
    }
    else {
        enableBearer = true;
    }
    return {
        initOptions: configuration?.initOptions || defaultInitOptions,
        config: configuration.config,
        enableBearerInterceptor: enableBearer
    };
};
class AmsSecurityModule {
    /**
     * Metodo para configurar el módulo de seguridad
     *
     * @author Antonio Garcia Gomez
     * @static
     * @param {?IAmsKeycloakFullConfig} config El objeto con la configuración a aplicar al módulo
     * @returns {ModuleWithProviders<AmsSecurityModule>} El módulo configurado
     * @throws Dispara un error si el módulo no está correctamente configurado
     */
    static forRoot(config) {
        const amsKeycloakFullConfigL = getAmsKeycloakFullConfig(config);
        const initOptions = amsKeycloakFullConfigL?.initOptions;
        const configL = amsKeycloakFullConfigL?.config;
        // Validamos que vengan las opciones de configuración necesarias
        if (!initOptions ||
            !initOptions?.onLoad ||
            !initOptions?.pkceMethod ||
            initOptions?.checkLoginIframe === undefined ||
            initOptions?.useNonce === undefined ||
            initOptions?.enableLogging === undefined) {
            throw new Error('Debe configurar la propiedad initOptions del módulo de keycloak');
        }
        if (!configL || !configL.url || !configL.realm || !configL.clientId) {
            throw new Error('Debe configurar la propiedad config del módulo de keycloak');
        }
        return {
            ngModule: AmsSecurityModule,
            providers: [
                { provide: 'AMSKEYCLOAKCONFIG', useValue: amsKeycloakFullConfigL },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializer,
                    multi: true,
                    deps: [KeycloakService, 'AMSKEYCLOAKCONFIG']
                }
            ]
        };
    }
}
AmsSecurityModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsSecurityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AmsSecurityModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: AmsSecurityModule, imports: [KeycloakAngularModule] });
AmsSecurityModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsSecurityModule, imports: [KeycloakAngularModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsSecurityModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [KeycloakAngularModule],
                    providers: [],
                    exports: []
                }]
        }] });

/**
 * Guarda para el control de rutas en función del estado de logado de KeyCloak del usuario
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsKeycloakGuard
 * @typedef {AmsKeycloakGuard}
 * @implements {CanLoad}
 * @implements {CanActivate}
 */
class AmsKeycloakGuard {
    /**
     * Creates an instance of AmsKeycloakGuard.
     *
     * @author Antonio Garcia Gomez
     * @param {KeycloakService} keycloakService El servicio de Keycloak
     */
    constructor(keycloakService) {
        this.keycloakService = keycloakService;
    }
    /**
     * Controla si la ruta se puede activar
     *
     * @author Antonio García Gómez
     * @param {ActivatedRouteSnapshot} route La ruta a validar
     * @throws Lanzará un error si la ruta no tiene un parámetro "amsKeycloakFullConfig" en el objeto data de la ruta
     *         (ActivatedRouteSnapshot.data --> Datos adicionales definidos por el desarrollador proporcionados
     *          al componente a través de ActivatedRoute)
     * @returns {AmsKeycloakGuarddMethodResponse} Devuelve si puede activar la ruta
     */
    canActivate(route) {
        if (route.data?.['amsKeycloakFullConfig']) {
            const amsKeycloakFullConfig = route.data['amsKeycloakFullConfig'];
            return this.keycloakService
                .init({
                config: amsKeycloakFullConfig.config,
                initOptions: amsKeycloakFullConfig.initOptions,
                enableBearerInterceptor: amsKeycloakFullConfig.enableBearerInterceptor
            })
                .then(() => this.keycloakService.isLoggedIn());
        }
        else {
            throw new Error('No ha sido enviado el objeto de tipo amsKeycloakFullConfig al activar el guard canActivate');
        }
    }
    /**
     * Controla si la ruta se puede cargar
     *
     * @author Antonio García Gómez
     * @param {Route} route la ruta a validar
     * @throws Lanzará un error si la ruta no tiene un parámetro "amsKeycloakFullConfig" en el objeto data de la ruta
     *         (ActivatedRouteSnapshot.data --> Datos adicionales definidos por el desarrollador proporcionados
     *          al componente a través de ActivatedRoute)
     * @returns {AmsKeycloakGuarddMethodResponse} Devuelve si puede cargar la ruta
     */
    canLoad(route) {
        if (route.data?.['amsKeycloakFullConfig']) {
            const amsKeycloakFullConfig = route.data['amsKeycloakFullConfig'];
            return this.keycloakService
                .init({
                config: amsKeycloakFullConfig.config,
                initOptions: amsKeycloakFullConfig.initOptions,
                enableBearerInterceptor: amsKeycloakFullConfig.enableBearerInterceptor
            })
                .then(() => this.keycloakService.isLoggedIn());
        }
        else {
            throw new Error('No ha sido enviado el objeto de tipo amsKeycloakFullConfig al activar el guard canLoad');
        }
    }
}
AmsKeycloakGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsKeycloakGuard, deps: [{ token: i1$4.KeycloakService }], target: i0.ɵɵFactoryTarget.Injectable });
AmsKeycloakGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsKeycloakGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsKeycloakGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$4.KeycloakService }]; } });

/**
 * Guarda para el control de rutas en función del navegador utilizado
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsNavigatorsCheckGuard
 * @typedef {AmsNavigatorsCheckGuard}
 * @implements {CanActivate}
 * @implements {CanActivateChild}
 * @implements {CanLoad}
 */
class AmsNavigatorsCheckGuard {
    /**
     * Creates an instance of AmsNavigatorsCheckGuard.
     *
     * @author Antonio Garcia Gomez
     * @param {Router} router El router para poder redireccionar en caso de navegador no compatible
     */
    constructor(router) {
        this.router = router;
    }
    /**
     * Hace una redirección a la ruta /browser-no-compatible y devuelve false
     *
     * @author Antonio García Gómez
     * @returns {boolean} Siempre devuelve falso
     */
    browserNoCompatible() {
        this.router.navigate(['browser-no-compatible']);
        return false;
    }
    /**
     * Controla la activación de la ruta
     *
     * @author Antonio García Gómez
     * @returns {AmsNavigatorsCheckGuardMethodRewsponse} Devuelve si puede activar la ruta
     */
    canActivate() {
        return this.checkDevice();
    }
    /**
     * Controla la activación de una ruta hija
     *
     * @author Antonio García Gómez
     * @returns {AmsNavigatorsCheckGuardMethodRewsponse} Devuelve si puede activar la ruta hija
     */
    canActivateChild() {
        return this.checkDevice();
    }
    /**
     * Controla si se puede hacer la carga de la ruta
     *
     * @author Antonio García Gómez
     * @returns {*} {AmsNavigatorsCheckGuardMethodRewsponse} Devuelve si puede cargar la ruta
     */
    canLoad() {
        return this.checkDevice();
    }
    /**
     * Verifica si el navegador es uno compatible (sólo internet explorer se da como no válido)
     * Si es IE, carga la ruta "browser-no-compatible" y devuelve false
     *
     * @author Antonio Garcia Gomez
     * @returns {(boolean | Promise<boolean>)} True si el navegador no es IE y false en caso contrario
     */
    checkDevice() {
        let rtv = true;
        if (this.isIE()) {
            rtv = this.browserNoCompatible();
        }
        return rtv;
    }
    /**
     * Comprueba si el navegador es Internet Explorer
     *
     * @author Antonio Garcia Gomez
     * @returns {boolean} true si el navegador es IE y false en caso contrario
     */
    isIE() {
        let isIE = false;
        if (window.document['documentMode']) {
            isIE = true;
        }
        // if (navigator.userAgent.includes('Trident')) {
        //   isIE = true;
        // }
        return isIE;
    }
}
AmsNavigatorsCheckGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsNavigatorsCheckGuard, deps: [{ token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
AmsNavigatorsCheckGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsNavigatorsCheckGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsNavigatorsCheckGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.Router }]; } });

const VALIDITY_TIME = 30;
const SESSION_STORAGE_KEY = 'X_AMS_Access';
/**
 * Servicio para la gestión de Keycloak, Login, Logout, recuperar instancia, tiempo de expiración del token y actualización del token
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsKeycloakService
 * @typedef {AmsKeycloakService}
 * @implements {IAKeycloakService}
 */
class AmsKeycloakService {
    /**
     * Creates an instance of AmsKeycloakService.
     *
     * @author Antonio Garcia Gomez
     * @param {KeycloakService} keycloakServ El servicio de keycloak
     */
    constructor(keycloakServ) {
        this.keycloakServ = keycloakServ;
        // Suscripción para ser limpiada
        this.subscription = new Subscription();
        // Token actualizado
        this.token$L = new BehaviorSubject(null);
        this.keycloakEvents$L = this.keycloakServ.keycloakEvents$;
        // Almacena el token inicial antes de que se dispare el evento de refresco
        this.storeToken();
        // Gestionamos vía observable los tokens que se van renovando en keycloak
        this.controlTokenUpdated();
    }
    /**
     * Devuelve los eventos de keycloak
     *
     * @author Antonio Garcia Gomez
     * @returns {Subject<IAmsKeycloakEvent>} Los eventos de Keycloak
     */
    get keycloakEvents$() {
        return this.keycloakEvents$L;
    }
    /**
     * Devuelve el token que se ha actualizado desde keycloak
     *
     * @author Antonio Garcia Gomez
     * @readonly
     * @type {Subject<string>}
     */
    get token$() {
        return this.token$L;
    }
    /**
     * Clear suscription
     *
     * @author Antonio Garcia Gomez
     */
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * Controla el que se actualice el observable del token cuando se dispara el evento de token caducado
     *
     * @author Antonio Garcia Gomez
     */
    controlTokenUpdated() {
        // Controlamos los eventos de keycloak
        if (this.keycloakEvents$) {
            this.subscription.add(this.keycloakEvents$.pipe(filter((e) => e.type === 3 /* AmsKeycloakEventType.OnAuthRefreshSuccess */)).subscribe({
                next: () => {
                    // Almacenamos los datos del token en el observable y en el sessionStorage
                    this.storeToken();
                }
            }));
        }
    }
    /**
     * Devuelve la instancia de la sesion iniciada de keycloak
     *
     * @author Antonio Garcia Gomez
     * @returns {KeycloakInstance} La instancia de keycloak
     */
    getKeycloakInstance() {
        return this.keycloakServ.getKeycloakInstance();
    }
    /**
     * Devuelve una promesa con el token actual que está usando Keycloak
     *
     * @author Antonio Garcia Gomez
     * @returns {Promise<string>} Una promesa con el token
     */
    getToken() {
        return this.keycloakServ.getToken();
    }
    /**
     * Devuelve si el usuario ya ha hecho el logado a través de keycloak
     *
     * @author Antonio Garcia Gomez
     * @returns {Promise<boolean>} Devuelve una promesa con un buleano indicando si el usuario está logado o no en keycloak
     */
    isLoggedIn() {
        return this.keycloakServ.isLoggedIn();
    }
    /**
     * Hace el login de keycloak
     *
     * @author Antonio Garcia Gomez
     * @param {?KeycloakLoginOptions} [options] Opciones para hacer el login en Keycloak
     * @returns {Promise<void>} Devuelve una promesa vacía después de hacer el login
     */
    login(options) {
        return this.keycloakServ.login(options);
    }
    /**
     * Hace el logout de keycloak
     *
     * @author Antonio Garcia Gomez
     * @returns {Promise<void>} Devuelve una promesa vacía después de hacer el logout
     */
    logout() {
        return this.keycloakServ.logout();
    }
    /**
     * Actualiza el observable que contendrá los tokens de keycloak y almacena el token actual en el sessionStorage
     *
     * @author Antonio Garcia Gomez
     */
    storeToken() {
        this.getToken().then((token) => {
            // Propagamos el token por el observable
            this.token$L.next(token);
            // Lo almacenamos en el local storage
            sessionStorage.setItem(SESSION_STORAGE_KEY, token);
        });
    }
    /**
     * Devuelve el tiempo de expiración del token de keycloak
     *
     * @author Antonio Garcia Gomez
     * @returns {number} El tiempo de expiración del token de keycloak
     */
    timeTokenExpired() {
        // Recuperamos el token de la instancia de keycloak
        const tokenParsed = this.keycloakServ.getKeycloakInstance().tokenParsed;
        let timeToExpired = 60;
        if (tokenParsed && tokenParsed.exp && tokenParsed.iat) {
            timeToExpired = tokenParsed.exp - tokenParsed.iat;
        }
        return timeToExpired;
    }
    /**
     * Actualiza el token de keycloak
     *
     * @author Antonio Garcia Gomez
     * @returns {Promise<boolean>} Devuelve una promesa con un buelano indicando si se ha actualizado el token
     */
    updateToken() {
        return this.keycloakServ.updateToken(this.timeTokenExpired() - VALIDITY_TIME);
    }
}
AmsKeycloakService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsKeycloakService, deps: [{ token: i1$4.KeycloakService }], target: i0.ɵɵFactoryTarget.Injectable });
AmsKeycloakService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsKeycloakService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsKeycloakService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$4.KeycloakService }]; } });

/* eslint-disable @typescript-eslint/member-ordering */
/**
 * An ActivateRoute test double with a `paramMap` observable.
 * Use the `setParamMap()` method to add the next `paramMap` value.
 */
class ActivatedRouteStub {
    /**
     * Creates an instance of ActivatedRouteStub.
     *
     * @author Antonio Garcia Gomez
     * @param {?Params} [initialParams] Parámetros iniciales
     */
    constructor(initialParams) {
        // Use a ReplaySubject to share previous values with subscribers
        // and pump new values into the `paramMap` observable
        this.subject = new ReplaySubject();
        /** The mock paramMap observable */
        this.paramMap = this.subject.asObservable();
        this.setParamMap(initialParams);
    }
    /**
     * Set the paramMap observable's next value
     *
     * @author Antonio Garcia Gomez
     * @param {Params} [params]
     */
    /**
     * Set the paramMap observable's next value
     *
     * @author Antonio Garcia Gomez
     * @param {Params} [params] the `Params` instance tobe converted to `ParamMap`
     */
    setParamMap(params = {}) {
        this.subject.next(convertToParamMap(params));
    }
}

/*
 * Mock async observables that return asynchronously.
 * The observable either emits once and completes or errors.
 *
 * Must call `tick()` when test with `fakeAsync()`.
 *
 * THE FOLLOWING DON'T WORK
 * Using `of().delay()` triggers TestBed errors;
 * see https://github.com/angular/angular/issues/10127 .
 *
 * Using `asap` scheduler - as in `of(value, asap)` - doesn't work either.
 */
/**
 * Create async observable that emits-once and completes
 * after a JS engine turn
 *
 * @author Antonio Garcia Gomez
 * @template T
 * @param {T} data Data object to be resolved
 * @returns {Observable<T>} The observable
 */
const asyncData = (data) => defer(() => Promise.resolve(data));
/**
 * Create async observable error that errors
 * after a JS engine turn
 *
 * @param errorObject El objeto de error
 */
/**
 * Create async observable error that errors
 * after a JS engine turn
 *
 * @author Antonio Garcia Gomez
 * @template T
 * @param {*} errorObject Error object to be resolved
 * @returns {Observable<T>} The observable
 */
const asyncError = (errorObject) => defer(() => Promise.reject(errorObject));

/* eslint-disable jsdoc/no-undefined-types */
const MIN = 0;
const MAX = 100;
/**
 * Add the toHaveTextMacher to jasmine matchers
 *
 * @author Antonio Garcia Gomez
 * @exports
 */
const addMatchers = () => {
    jasmine.addMatchers({
        toHaveText
    });
};
/**
 * Compose a message with the params
 *
 * @author Antonio Garcia Gomez
 * @param {string} actualText the element text
 * @param {string} expectedText the expected text
 * @param {?*} [expectationFailOutput] the expectaction failoutput text
 * @returns {string} the composed text
 */
const composeMessage = (actualText, expectedText, expectationFailOutput) => {
    const a = actualText.length < MAX ? actualText : actualText.slice(MIN, MAX) + '...';
    const efo = expectationFailOutput ? ` '${expectationFailOutput}'` : '';
    return `Expected element to have text content '${expectedText}' instead of '${a}'${efo}`;
};
/**
 * Description placeholder
 *
 * @author Antonio Garcia Gomez
 * @returns {jasmine.CustomMatcher} the custom matcher
 */
const toHaveText = () => ({
    compare: (actual, expectedText, expectationFailOutput) => {
        const actualText = elementText(actual);
        const pass = actualText.indexOf(expectedText) > -1;
        const message = pass ? '' : composeMessage(actualText, expectedText, expectationFailOutput);
        return { pass, message };
    }
});
/**
 * Description placeholder
 *
 * @author Antonio Garcia Gomez
 * @param {*} n element to get the text
 * @returns {string} the text content of the element received
 */
const elementText = (n) => {
    let returnV;
    if (n instanceof Array) {
        returnV = n.map(elementText).join('');
    }
    else if (n.nodeType === Node.COMMENT_NODE) {
        returnV = '';
    }
    else if (n.nodeType === Node.ELEMENT_NODE && n.hasChildNodes()) {
        returnV = elementText(Array.prototype.slice.call(n.childNodes));
    }
    else if (n.nativeElement) {
        n = n.nativeElement;
        returnV = n.textContent;
    }
    else {
        returnV = null;
    }
    return returnV;
};

/* eslint-disable @angular-eslint/directive-selector */
class RouterLinkDirective {
    constructor() {
        this.navigatedTo = null;
    }
    /**
     * Controls click event of the element that use the directive
     *
     * @author Antonio Garcia Gomez
     */
    onClick() {
        this.navigatedTo = this.linkParams;
    }
}
RouterLinkDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: RouterLinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
RouterLinkDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.4", type: RouterLinkDirective, selector: "[routerLink]", inputs: { linkParams: "linkParams" }, host: { listeners: { "click": "onClick()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: RouterLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[routerLink]'
                }]
        }], propDecorators: { linkParams: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });
class RouterStubsModule {
}
RouterStubsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: RouterStubsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RouterStubsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: RouterStubsModule, declarations: [RouterLinkDirective] });
RouterStubsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: RouterStubsModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: RouterStubsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [RouterLinkDirective]
                }]
        }] });

///// Short utilities /////
/**
 * Wait a tick, then detect changes
 *
 * @author Antonio Garcia Gomez
 * @param {ComponentFixture<any>} f Component where trigger a change detection cycle
 */
const advance = (f) => {
    tick();
    f.detectChanges();
};
// See https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
/**
 * Button events to pass to `DebugElement.triggerEventHandler` for RouterLink event handler
 *
 * @author Antonio Garcia Gomez
 * @type {{ left: { button: number; }; right: { button: number; }; }}
 */
const ButtonClickEvents = {
    left: { button: 0 },
    right: { button: 2 }
};
/**
 * Simulate element click. Defaults to mouse left-button click event.
 *
 * @author Antonio Garcia Gomez
 * @param {(DebugElement | HTMLElement)} el HTMLElement where simulate click
 * @param {*} [eventObj] Event to be simulated
 */
const clickElement = (el, eventObj = ButtonClickEvents.left) => {
    if (el instanceof HTMLElement) {
        el.click();
    }
    else {
        el.triggerEventHandler('click', eventObj);
    }
};

/**
 * Módulo de utilidades
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsUtilsModule
 * @typedef {AmsUtilsModule}
 */
class AmsUtilsModule {
}
AmsUtilsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsUtilsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AmsUtilsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: AmsUtilsModule });
AmsUtilsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsUtilsModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsUtilsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    exports: []
                }]
        }] });

/* eslint-disable space-before-function-paren */
/**
 * Function to clean the subscription of the recevied property
 *
 * @author Antonio Garcia Gomez
 * @param {*} property The property to be unsubscribed
 */
const unsuscribeProperty = (property) => {
    // Variables of type suscription
    if (property && isFunction(property.unsubscribe)) {
        property.unsubscribe();
    }
    // Arrays of suscriptions
    if (property &&
        Array.isArray(property) &&
        property.length > 0 &&
        property.every((item) => isFunction(item.unsubscribe))) {
        property.forEach((sub) => {
            if (sub) {
                sub.unsubscribe();
            }
        });
    }
};
/**
 * Function/Anotattion to be used in components to auto unsubscribe any property defined in the component annotated
 * For example:
 * "@AutoUnsubscribe()" in the component definition. You only have to set the annotation and every variable of type Suscription
 * or arrays of suscriptions will be unsubscribed automatically
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @param {string[]} [suscriptionList] If we want to use a list of variables to be unsubscribe. This is faster and more optimized
 *                                        that iterates over any variable defined in the component
 * @param {string[]} [blackList] If we don't want to unsuscribed the variables included in the array
 * @param {string} [event] If we want to use another event to unsuscribe the variables
 * @returns {(constructor: any) => void} Returns the component class with the event vitaminized with the clean subscriptions code
 */
function AutoUnsubscribe(suscriptionList = [], blackList = [], event = 'ngOnDestroy') {
    return function (constructor) {
        const original = constructor.prototype[event];
        // Overwrite the event/function. By default 'ngOnDestroy'
        constructor.prototype[event] = function () {
            // Calls the original event (by default 'ngOndestroy')
            if (original && isFunction(original)) {
                original.apply(this, arguments);
            }
            //   isFunction(original) && original.apply(this, arguments);
            // If we want to clean a list of Suscription variables
            if (suscriptionList && suscriptionList.length > 0) {
                suscriptionList.forEach((prop) => {
                    if (this.hasOwnProperty(prop)) {
                        const property = this[prop];
                        unsuscribeProperty(property);
                    }
                    else {
                        const errorL = `${constructor.name} is using @AutoUnsubscribe but does not have the property ${prop}`;
                        console.error(errorL);
                        throw new Error(errorL);
                    }
                });
                // If we want to clean all variables of type Suscription or Suscription[]
            }
            else {
                for (const prop in this) {
                    if (this.hasOwnProperty(prop) && !blackList.includes(prop)) {
                        const property = this[prop];
                        unsuscribeProperty(property);
                    }
                }
            }
        };
    };
}

const isFunction = (fn) => typeof fn === 'function';

/**
 * Función que si se usa como valor por defecto para un parámetro, lo hace obligatorio
 *
 * @author Antonio Garcia Gomez
 * @example const print = (num = isRequired()) => { console.log(`printing ${num}`) };
 * @throws Un error con el texto de parámetro obligario
 */
const isRequired = () => {
    throw new Error('Parámetro obligatorio');
};

/**
 * Servicio para el control y deshabilitación de de los métodos de consola log, info, warn, error, memory, assert, clear, count,
 * countReset, debug, dir, dirxml, exception, group, groupCollapsed, groupEnd, table, time, timeEnd, timeLog, timeStamp, trace,
 * profile y profileEnd
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsConsoleUtilsService
 * @typedef {AmsConsoleUtilsService}
 */
class AmsConsoleUtilsService {
    /**
     * Evita que se saquen mensajes por consola y saca un mensaje informativo
     *
     * @author Antonio Garcia Gomez
     * @static
     */
    static overrideConsoleMethods() {
        const showForbiddenMsg = (oldCons) => {
            oldCons.log('%cPor su seguridad, hemos deshabilitado la consola!', 'color: red; font-family: sans-serif; font-size: 2em; font-weight: bolder; text-shadow: #000 1px 1px;');
            oldCons.log('%c ', 'font-size:400px;' +
                'background:url("http://t3.gstatic.com/images?q=tbn:ANd9GcTqeOHYBm0i7Oeny8APKfIY8c5A_Q0nvGKch_LZNil3RWUlwrJT");' +
                ' background-size:contain; background-repeat:no-repeat');
        };
        const console = ((oldCons) => ({
            log: () => {
                showForbiddenMsg(oldCons);
            },
            info: () => {
                showForbiddenMsg(oldCons);
            },
            warn: () => {
                showForbiddenMsg(oldCons);
            },
            error: () => {
                showForbiddenMsg(oldCons);
            },
            memory: () => {
                showForbiddenMsg(oldCons);
            },
            assert: () => {
                showForbiddenMsg(oldCons);
            },
            clear: () => {
                showForbiddenMsg(oldCons);
            },
            count: () => {
                showForbiddenMsg(oldCons);
            },
            countReset: () => {
                showForbiddenMsg(oldCons);
            },
            debug: () => {
                showForbiddenMsg(oldCons);
            },
            dir: () => {
                showForbiddenMsg(oldCons);
            },
            dirxml: () => {
                showForbiddenMsg(oldCons);
            },
            exception: () => {
                showForbiddenMsg(oldCons);
            },
            group: () => {
                showForbiddenMsg(oldCons);
            },
            groupCollapsed: () => {
                showForbiddenMsg(oldCons);
            },
            groupEnd: () => {
                showForbiddenMsg(oldCons);
            },
            table: () => {
                showForbiddenMsg(oldCons);
            },
            time: () => {
                showForbiddenMsg(oldCons);
            },
            timeEnd: () => {
                showForbiddenMsg(oldCons);
            },
            timeLog: () => {
                showForbiddenMsg(oldCons);
            },
            timeStamp: () => {
                showForbiddenMsg(oldCons);
            },
            trace: () => {
                showForbiddenMsg(oldCons);
            },
            profile: () => {
                showForbiddenMsg(oldCons);
            },
            profileEnd: () => {
                showForbiddenMsg(oldCons);
            }
        }))(window.console);
        window.console = console;
    }
    /**
     * Sustituye los métodos alert por console.log y elimina los confirm y los prompt
     *
     * @author Antonio Garcia Gomez
     * @static
     */
    static overrideAlertMethods() {
        window.alert = () => {
            console.log();
        };
        window.confirm = null;
        window.prompt = null;
    }
}
AmsConsoleUtilsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsConsoleUtilsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AmsConsoleUtilsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsConsoleUtilsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsConsoleUtilsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * Servicio para la carga dinámica de scripts en tiempo de ejecución
 *
 * @author Antonio Garcia Gomez
 * @exports
 * @class AmsScriptService
 * @typedef {AmsScriptService}
 * @implements {IScriptService}
 */
class AmsScriptService {
    /**
     * Función que añade al head de la página los scripts que recibimos como parámetro
     *
     * @author Antonio Garcia Gomez
     * @param {IAmsScript[]} arrayScripts el array de los scripts que queremos añadir al head
     * @example
     *   const script: Script[] = [
     *     {
     *       type: 'text/javascript',
     *       innerHTML: `alert('${this.textAlert}')`
     *     }
     *   ];
     *
     *   this.scriptServ.loadScripts(script);
     *   -------------------------------------------------------------------------------
     *   const script: Script[] = [
     *     {
     *       src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
     *       type: 'text/javascript',
     *       async: true,
     *       attributes: [
     *         {
     *           name: 'data-domain-script',
     *           value: '3c564ff1-6e51-407f-a6ff-9132cce7e549'
     *         }
     *       ]
     *     },
     *     {
     *       type: 'text/javascript',
     *       innerHTML: 'function OptanonWrapper() { }'
     *     }
     *   ];
     *
     *   this.scriptServ.loadScripts(script);
     */
    loadScripts(arrayScripts) {
        arrayScripts.forEach((script) => {
            if (script) {
                this.loadScript(script);
            }
        });
    }
    /**
     * Método que carga el script que recibimos como parámetro
     *
     * @author Antonio Garcia Gomez
     * @private
     * @param {IAmsScript} script El objeto de tipo Script que vamos a cargar
     */
    loadScript(script) {
        const node = document.createElement('script');
        for (const property in script) {
            if (property !== 'attributes') {
                node[property] = script[property];
            }
            else {
                for (const attribute in script[property]) {
                    if (script[property].hasOwnProperty(attribute)) {
                        const nameAttribute = script[property][attribute].name;
                        const valueAttribute = script[property][attribute].value;
                        node.setAttribute(nameAttribute, valueAttribute);
                    }
                }
            }
        }
        document.getElementsByTagName('head')[0].appendChild(node);
    }
}
AmsScriptService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsScriptService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AmsScriptService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsScriptService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: AmsScriptService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * @file Api pública de la librería.
 * @author Antonio García Gómez
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ActivatedRouteStub, AmsAnalyticsModule, AmsAnalyticsService, AmsConsoleUtilsService, AmsErrorHandlerModule, AmsErrorHandlerService, AmsHttpClientAuthTypeToken, AmsHttpClientExtraOptions, AmsHttpClientModule, AmsHttpClientService, AmsHttpElapsedTimeService, AmsHttpPageLoaderService, AmsKeycloakGuard, AmsKeycloakService, AmsLoggerConfig, AmsLoggerLevel, AmsLoggerModule, AmsLoggerService, AmsLoggerViewService, AmsNavigatorsCheckGuard, AmsPipesModule, AmsSafePipe, AmsScriptService, AmsSecurityModule, AmsUtilsModule, AutoUnsubscribe, ButtonClickEvents, DEFAULT_COLOR_SCHEME, RouterLinkDirective, RouterStubsModule, addMatchers, advance, amsHttpClientCreator, asyncData, asyncError, clickElement, isFunction, isRequired };
